import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Heart, Calendar, MapPin, Gift, Users, Clock, Check, 
  CreditCard, Mail, User, ChevronDown,
  Camera, X, ChevronLeft, ChevronRight, ExternalLink, Copy, QrCode,
  Loader2
} from 'lucide-react';
import { TEMPLATE_THEME_MAP, TEMPLATE_LOOKUP, resolveTemplateId } from '../lib/weddingTemplates';
import { TEMPLATE_PREVIEW_CONTENT, TEMPLATE_PREVIEW_COLORS } from '../lib/templatePreviewContent';
import { normalizeDisplayText, normalizeObjectStrings } from '../lib/utils';
import { trackEvent } from '../lib/analytics';
import SEOHead from '../components/SEOHead';
import { getPublicSiteSeoConfig } from '../lib/seo';
import brandMark from '../assets/virei-noiva-mark.svg';
import WeddingTemplateRenderer from '../templates/wedding/WeddingTemplateRenderer';
import { isStructuredTemplateId } from '../templates/wedding/structuredTemplateIds';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API = `${BACKEND_URL}/api`;
const MP_PUBLIC_KEY = process.env.REACT_APP_MP_PUBLIC_KEY || '';
const PAGARME_PUBLIC_KEY = process.env.REACT_APP_PAGARME_PUBLIC_KEY || '';
const GIFT_PAYMENT_PROVIDER = (process.env.REACT_APP_GIFT_PAYMENT_PROVIDER || process.env.REACT_APP_PAYMENT_PROVIDER || 'asaas').toLowerCase();
const CARD_FLOW_TOKEN_ONLY = (process.env.REACT_APP_CARD_FLOW_TOKEN_ONLY || 'true').toLowerCase() === 'true';
const FALLBACK_GIFT_IMAGE = 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=1200';
const FALLBACK_HERO_IMAGE = 'https://images.unsplash.com/photo-1519741497674-611481863552?w=1920';
const HTML_EXACT_DECOR_TOP = 'https://cdn0.casamentos.com.br/website/fotos/templates/180_home.png?v=c10dfde9501c0359853ad0dbcf4ab591';
const HTML_EXACT_SEPARATOR = 'https://cdn0.casamentos.com.br/website/fotos/templates/180_separator.png?v=c10dfde9501c0359853ad0dbcf4ab591';
const BRL_FORMATTER = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const formatBRL = (value) => BRL_FORMATTER.format(Number(value || 0));
const FONT_FAMILY_BY_ID = {
  playfair: "'Playfair Display', serif",
  dancing: "'Dancing Script', cursive",
  'great-vibes': "'Great Vibes', cursive",
  cormorant: "'Cormorant Garamond', serif",
  lora: "'Lora', serif",
  montserrat: "'Montserrat', sans-serif",
  raleway: "'Raleway', sans-serif",
  josefin: "'Josefin Sans', sans-serif",
  mulish: "'Mulish', sans-serif",
  'eb-garamond': "'EB Garamond', serif",
  didact: "'Didact Gothic', sans-serif",
  poiret: "'Poiret One', cursive",
  syne: "'Syne', sans-serif",
  jost: "'Jost', sans-serif",
  spectral: "'Spectral', serif",
  italiana: "'Italiana', serif",
  karla: "'Karla', sans-serif",
  anton: "'Anton', sans-serif",
  'dm-sans': "'DM Sans', sans-serif",
  'noto-serif-jp': "'Noto Serif JP', serif",
  'source-sans-3': "'Source Sans 3', sans-serif",
  merriweather: "'Merriweather', serif",
  lato: "'Lato', sans-serif",
  'libre-baskerville': "'Libre Baskerville', serif"
};

const normalizeHexColor = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) return trimmed.toUpperCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(trimmed)) {
    return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`.toUpperCase();
  }
  return null;
};

const getAutoContrastTextColor = (baseColor, fallbackColor = '#FFFFFF') => {
  const normalized = normalizeHexColor(baseColor);
  if (!normalized) return fallbackColor;
  const numeric = Number.parseInt(normalized.slice(1), 16);
  const red = (numeric >> 16) & 255;
  const green = (numeric >> 8) & 255;
  const blue = numeric & 255;
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  return luminance > 0.58 ? '#1A1A1A' : '#FFFFFF';
};


const TEMPLATE_RENDER_PROFILES = {
  'classic-editorial': { heroLayout: 'framed', storyLayout: 'journal', countdownStyle: 'micro', locationLayout: 'cards', giftStyle: 'paper', rsvpStyle: 'floating', sectionTone: 'ivory' },
  'botanical-garden': { heroLayout: 'split', storyLayout: 'polaroid', countdownStyle: 'wreath', locationLayout: 'paper', giftStyle: 'soft', rsvpStyle: 'card', sectionTone: 'botanical' },
  'modern-black-tie': { heroLayout: 'full', storyLayout: 'timeline', countdownStyle: 'ticker', locationLayout: 'glow', giftStyle: 'glass', rsvpStyle: 'outline', sectionTone: 'midnight' },
  'destination-praia': { heroLayout: 'airy', storyLayout: 'waves', countdownStyle: 'inline', locationLayout: 'watercolor', giftStyle: 'outline', rsvpStyle: 'floating', sectionTone: 'sea' },
  'minimal-classic': { heroLayout: 'minimal', storyLayout: 'journal', countdownStyle: 'micro', locationLayout: 'cards', giftStyle: 'outline', rsvpStyle: 'block', sectionTone: 'paper' },
  'ethereal-grid': { heroLayout: 'editorial', storyLayout: 'grid', countdownStyle: 'inline', locationLayout: 'cards', giftStyle: 'outline', rsvpStyle: 'floating', sectionTone: 'paper' },
  'asymmetric-flow': { heroLayout: 'split', storyLayout: 'offset', countdownStyle: 'weight', locationLayout: 'overlay', giftStyle: 'soft', rsvpStyle: 'block', sectionTone: 'cream' },
  'brutalist-chic': { heroLayout: 'full', storyLayout: 'blocks', countdownStyle: 'ticker', locationLayout: 'split-map', giftStyle: 'sharp', rsvpStyle: 'outline', sectionTone: 'concrete' },
  'dark-editorial': { heroLayout: 'full', storyLayout: 'bars', countdownStyle: 'floating', locationLayout: 'glow', giftStyle: 'glass', rsvpStyle: 'glow', sectionTone: 'obsidian' },
  'boho-pampas': { heroLayout: 'arch', storyLayout: 'polaroid', countdownStyle: 'wreath', locationLayout: 'paper', giftStyle: 'paper', rsvpStyle: 'card', sectionTone: 'warm' },
  'coastal-dune': { heroLayout: 'airy', storyLayout: 'waves', countdownStyle: 'water', locationLayout: 'watercolor', giftStyle: 'bleach', rsvpStyle: 'card', sectionTone: 'sea' },
  'mountain-retreat': { heroLayout: 'panoramic', storyLayout: 'panels', countdownStyle: 'ridge', locationLayout: 'pine', giftStyle: 'wood', rsvpStyle: 'card', sectionTone: 'forest' },
  'dried-floral-arch': { heroLayout: 'arch', storyLayout: 'leaf-dividers', countdownStyle: 'lantern', locationLayout: 'cards', giftStyle: 'floral', rsvpStyle: 'card', sectionTone: 'petal' },
  'royal-court': { heroLayout: 'framed', storyLayout: 'vintage-frames', countdownStyle: 'medallion', locationLayout: 'cards', giftStyle: 'ornate', rsvpStyle: 'block', sectionTone: 'royal' },
  'ivory-classic': { heroLayout: 'framed', storyLayout: 'overlap', countdownStyle: 'micro', locationLayout: 'classic-map', giftStyle: 'outline', rsvpStyle: 'block', sectionTone: 'ivory' },
  'premium-wedding-experience': { heroLayout: 'premium-showcase', storyLayout: 'journal', countdownStyle: 'micro', locationLayout: 'paper', giftStyle: 'paper', rsvpStyle: 'floating', sectionTone: 'ivory' },
  'vintage-lace': { heroLayout: 'framed', storyLayout: 'sepia', countdownStyle: 'scroll', locationLayout: 'cards', giftStyle: 'illustrated', rsvpStyle: 'card', sectionTone: 'lace' },
  'midnight-ballroom': { heroLayout: 'full', storyLayout: 'timeline', countdownStyle: 'symmetry', locationLayout: 'glow', giftStyle: 'crystal', rsvpStyle: 'glow', sectionTone: 'midnight' },
  'vibrant-retro': { heroLayout: 'split', storyLayout: 'bubbles', countdownStyle: 'flip', locationLayout: 'pop', giftStyle: 'confetti', rsvpStyle: 'block', sectionTone: 'retro' },
  'pastel-love': { heroLayout: 'airy', storyLayout: 'clouds', countdownStyle: 'bubbles', locationLayout: 'cards', giftStyle: 'soft', rsvpStyle: 'card', sectionTone: 'pastel' },
  'illustrative-day': { heroLayout: 'editorial', storyLayout: 'drawn-timeline', countdownStyle: 'illustrated', locationLayout: 'cards', giftStyle: 'outlined', rsvpStyle: 'card', sectionTone: 'storybook' },
  'geometric-punch': { heroLayout: 'split', storyLayout: 'blocks', countdownStyle: 'geometric', locationLayout: 'split-map', giftStyle: 'sharp', rsvpStyle: 'block', sectionTone: 'contrast' },
  'digital-dream': { heroLayout: 'minimal', storyLayout: 'timeline', countdownStyle: 'matrix', locationLayout: 'overlay', giftStyle: 'glass', rsvpStyle: 'glow', sectionTone: 'digital' },
  'travel-log': { heroLayout: 'panoramic', storyLayout: 'journal', countdownStyle: 'globe', locationLayout: 'classic-map', giftStyle: 'ticket', rsvpStyle: 'ticket', sectionTone: 'travel' },
  'botanical-sketch': { heroLayout: 'minimal', storyLayout: 'leaf-dividers', countdownStyle: 'leaves', locationLayout: 'paper', giftStyle: 'outline', rsvpStyle: 'floating', sectionTone: 'botanical' },
  'watercolor-bloom': { heroLayout: 'airy', storyLayout: 'washes', countdownStyle: 'paint', locationLayout: 'watercolor', giftStyle: 'wash', rsvpStyle: 'card', sectionTone: 'watercolor' }
};

const getSafeImageSrc = (src) => (src && String(src).trim() ? src : FALLBACK_GIFT_IMAGE);

const withImageFallback = (event) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = FALLBACK_GIFT_IMAGE;
};

const buildTemplatePreviewSite = (forcedTemplateId = 'ivory-classic') => {
  const resolvedTemplateId = resolveTemplateId(forcedTemplateId);
  const templateColors = TEMPLATE_THEME_MAP[resolvedTemplateId] || {};

  return {
    ...TEMPLATE_PREVIEW_CONTENT,
    ...TEMPLATE_PREVIEW_COLORS,
    template_id: resolvedTemplateId,
    primary_color: templateColors.primary || TEMPLATE_PREVIEW_COLORS.primary_color,
    secondary_color: templateColors.secondary || TEMPLATE_PREVIEW_COLORS.secondary_color,
    text_color: templateColors.primary || TEMPLATE_PREVIEW_COLORS.text_color,
    banner_title_color: '',
    banner_title_font_family: '',
    banner_text_color: '',
    banner_text_font_family: '',
    banner_section_background_color: '',
    confirm_button_background_color: '',
    confirm_button_text_color: '',
    confirm_button_border_color: '',
    story_title_color: '',
    story_title_font_family: '',
    story_text_font_family: '',
    story_section_background_color: '',
    gallery_title_color: '',
    gallery_icon_color: '',
    gallery_section_background_color: '',
    gift_title_color: '',
    gift_text_color: '',
    gift_icon_color: '',
    gift_section_background_color: '',
    gift_card_background_color: '',
    gift_price_color: '',
    gift_button_background_color: '',
    gift_button_text_color: '',
    location_title_color: '',
    location_title_font_family: '',
    location_text_color: '',
    location_text_font_family: '',
    location_section_background_color: '',
    location_card_background_color: '',
    location_icon_color: '',
    rsvp_section_background_color: '',
    rsvp_text_color: '',
    rsvp_title_font_family: '',
    rsvp_text_font_family: '',
    rsvp_icon_color: ''
  };
};

export default function PublicSite() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const templateId = searchParams.get('template');
  const isTemplatePreview = slug === 'exemplo' && Boolean(templateId);
  
  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState(null);
  const [error, setError] = useState(null);
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftCheckoutStatus, setGiftCheckoutStatus] = useState({ loading: false, available: false, maintenance: false });
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [selectedGift, setSelectedGift] = useState(null);

  // RSVP Form
  const [rsvpData, setRsvpData] = useState({
    guest_name: '',
    guest_email: '',
    guests_count: 1,
    attending: true,
    message: ''
  });
  
  // RSVP Mode State
  const [rsvpMode, setRsvpMode] = useState('open');
  const [rsvpStep, setRsvpStep] = useState('name'); // name, search_result, confirm, success
  const [searchResults, setSearchResults] = useState([]);
  const [searchingGuest, setSearchingGuest] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);

  // Payment Form
  const [paymentData, setPaymentData] = useState({
    payer_name: '',
    payer_email: '',
    payer_phone: '',
    payment_method: 'pix',
    doc_type: 'CPF',
    doc_number: ''
  });

  // PIX Payment State
  const [pixPayment, setPixPayment] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  
  // Card Payment State
  const [cardPayment, setCardPayment] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [selectedInstallment, setSelectedInstallment] = useState(1);
  const [cardCheckoutMode, setCardCheckoutMode] = useState(null);
  const [cardFeeResponsibility, setCardFeeResponsibility] = useState('payer');
  const [cardEnabledForGift, setCardEnabledForGift] = useState(null);
  const [cardUnavailableReason, setCardUnavailableReason] = useState('');
  const [paymentStep, setPaymentStep] = useState('info'); // info, method, card_form, processing
  const [processingCard, setProcessingCard] = useState(false);
  
  // Card Form State
  const [cardFormData, setCardFormData] = useState({
    cardNumber: '',
    cardExpiry: '',
    cardCvv: '',
    cardholderName: '',
    cardholderPhone: '',
    docType: 'CPF',
    docNumber: '',
    postalCode: '',
    addressNumber: '',
    addressComplement: '',
    addressStreet: '',
    addressNeighborhood: '',
    addressCity: '',
    addressState: ''
  });
  const [cardError, setCardError] = useState('');
  const [postalCodeLookup, setPostalCodeLookup] = useState({ status: 'idle', message: '' });
  const [editingCardAddress, setEditingCardAddress] = useState(false);
  const [mpInstance, setMpInstance] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const seoConfig = getPublicSiteSeoConfig(site, slug, {
    noindex: isTemplatePreview || Boolean(site?.preview_unpublished),
  });
  const providerSupportsCard = GIFT_PAYMENT_PROVIDER === 'asaas'
    ? true
    : GIFT_PAYMENT_PROVIDER === 'mercadopago'
      ? Boolean(MP_PUBLIC_KEY)
      : GIFT_PAYMENT_PROVIDER === 'pagarme' && Boolean(PAGARME_PUBLIC_KEY);
  const resolvedCardCheckoutMode = cardCheckoutMode || (GIFT_PAYMENT_PROVIDER === 'asaas' && CARD_FLOW_TOKEN_ONLY ? 'redirect' : 'inline');
  const cardUsesHostedCheckout = resolvedCardCheckoutMode === 'redirect';
  const fallbackCardEnabled = providerSupportsCard;
  const cardPaymentEnabled = cardEnabledForGift === null ? fallbackCardEnabled : Boolean(cardEnabledForGift);
  const normalizedCardFeeResponsibility = cardFeeResponsibility === 'couple' ? 'couple' : 'payer';
  const coupleAbsorbsCardFee = normalizedCardFeeResponsibility === 'couple';
  const cardHintText = cardPaymentEnabled
    ? (cardUsesHostedCheckout
      ? 'Checkout seguro no provedor'
      : (coupleAbsorbsCardFee ? 'Até 12x no cartão sem acréscimo para o convidado' : 'Até 12x no cartão'))
    : (cardUnavailableReason || 'Indisponivel no momento');
  const selectedInstallmentOption = installments.find((item) => item.installments === selectedInstallment);
  const previewRegisterHref = `/register?template=${encodeURIComponent(resolveTemplateId(templateId || 'ivory-classic'))}`;
  const cardPostalCodeDigits = cardFormData.postalCode.replace(/\D/g, '');
  const hasCompleteCardAddress = Boolean(
    cardFormData.addressStreet &&
    cardFormData.addressNeighborhood &&
    cardFormData.addressCity &&
    cardFormData.addressState.length === 2
  );
  const showManualCardAddress = GIFT_PAYMENT_PROVIDER === 'pagarme' && (
    editingCardAddress ||
    postalCodeLookup.status === 'error' ||
    postalCodeLookup.status === 'partial'
  );
  const collectingPagarmeAddress = paymentStep === 'card_form' || (
    paymentStep === 'method' && paymentData.payment_method === 'pix'
  );

  useEffect(() => {
    fetchSite();
  }, [slug]);

  useEffect(() => {
    if (GIFT_PAYMENT_PROVIDER !== 'pagarme' || !collectingPagarmeAddress) {
      return undefined;
    }

    const postalCode = cardFormData.postalCode.replace(/\D/g, '');
    if (postalCode.length !== 8) {
      setPostalCodeLookup({ status: 'idle', message: '' });
      setEditingCardAddress(false);
      return undefined;
    }

    const controller = new AbortController();
    const lookupTimeout = window.setTimeout(async () => {
      setPostalCodeLookup({ status: 'loading', message: 'Buscando endereço...' });

      try {
        const response = await fetch(`https://viacep.com.br/ws/${postalCode}/json/`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error('Não foi possível consultar o CEP.');
        }

        const address = await response.json();
        if (address.erro) {
          throw new Error('CEP não encontrado. Confira ou informe o endereço manualmente.');
        }

        const addressFields = {
          addressStreet: String(address.logradouro || '').trim(),
          addressNeighborhood: String(address.bairro || '').trim(),
          addressCity: String(address.localidade || '').trim(),
          addressState: String(address.uf || '').trim().toUpperCase(),
        };
        const isComplete = Boolean(
          addressFields.addressStreet &&
          addressFields.addressNeighborhood &&
          addressFields.addressCity &&
          addressFields.addressState.length === 2
        );

        setCardFormData(prev => ({ ...prev, ...addressFields }));
        setPostalCodeLookup({
          status: isComplete ? 'success' : 'partial',
          message: isComplete
            ? 'Endereço preenchido automaticamente.'
            : 'Complete os dados que não foram encontrados para este CEP.',
        });
        setEditingCardAddress(!isComplete);
      } catch (error) {
        if (error.name === 'AbortError') return;
        setPostalCodeLookup({
          status: 'error',
          message: error.message || 'Não foi possível consultar o CEP. Informe o endereço manualmente.',
        });
        setEditingCardAddress(true);
      }
    }, 350);

    return () => {
      window.clearTimeout(lookupTimeout);
      controller.abort();
    };
  }, [cardFormData.postalCode, collectingPagarmeAddress]);

  // Load MercadoPago SDK script
  useEffect(() => {
    if (GIFT_PAYMENT_PROVIDER !== 'mercadopago' || !cardPaymentEnabled) {
      return undefined;
    }

    if (!window.MercadoPago && MP_PUBLIC_KEY) {
      const script = document.createElement('script');
      script.src = 'https://sdk.mercadopago.com/js/v2';
      script.async = true;
      script.onload = () => {
        if (window.MercadoPago) {
          const mp = new window.MercadoPago(MP_PUBLIC_KEY);
          setMpInstance(mp);
        }
      };
      document.body.appendChild(script);
      
      return () => {
        // Cleanup on unmount
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    } else if (window.MercadoPago && !mpInstance) {
      const mp = new window.MercadoPago(MP_PUBLIC_KEY);
      setMpInstance(mp);
    }

    return undefined;
  }, [cardPaymentEnabled, mpInstance]);

  // Auto-polling for payment status
  useEffect(() => {
    if (pixPayment?.payment_id && !pollingInterval) {
      // Start polling every 5 seconds
      const interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/payments/${pixPayment.payment_id}/status`);
          if (response.data.status === 'approved') {
            clearInterval(interval);
            setPollingInterval(null);
            trackEvent('purchase_completed', {
              payment_method: 'pix',
              gift_id: selectedGift?.gift_id,
              gift_name: selectedGift?.name,
              value: selectedGift?.price,
              slug,
            });
            toast.success('Pagamento confirmado! Obrigado pelo presente!');
            setShowGiftModal(false);
            setPixPayment(null);
            fetchSite();
          }
        } catch (error) {
          console.log('Polling error:', error);
        }
      }, 5000);
      
      setPollingInterval(interval);
    }
    
    // Cleanup on unmount or when pixPayment changes
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pixPayment?.payment_id]);

  const fetchSite = async () => {
    try {
      const response = await axios.get(`${API}/sites/public/${slug}`);
      setSite(normalizeObjectStrings(response.data));
      
      // Fetch RSVP mode
      try {
        const modeRes = await axios.get(`${API}/rsvp/${slug}/mode`);
        setRsvpMode(modeRes.data.rsvp_mode || 'open');
      } catch (e) {
        setRsvpMode('open');
      }
    } catch (error) {
      if (isTemplatePreview) {
        setSite(buildTemplatePreviewSite(resolveTemplateId(templateId)));
        setError(null);
        setRsvpMode('open');
      } else {
        setError(error.response?.status === 404 ? 'Site não encontrado' : 'Erro ao carregar site');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearchGuest = async () => {
    if (!rsvpData.guest_name.trim()) return;
    
    setSearchingGuest(true);
    try {
      const response = await axios.post(`${API}/rsvp/${slug}/search`, {
        guest_name: rsvpData.guest_name.trim()
      });
      
      if (response.data.found) {
        setSearchResults(normalizeObjectStrings(response.data.guests));
        setRsvpStep('search_result');
      } else {
        setSearchResults([]);
        setRsvpStep('not_found');
      }
    } catch (error) {
      toast.error('Erro ao buscar nome');
    } finally {
      setSearchingGuest(false);
    }
  };

  const handleSelectGuest = (guest) => {
    setSelectedGuest(guest);
    setRsvpData(prev => ({ ...prev, guest_name: normalizeDisplayText(guest.name) }));
    setRsvpStep('confirm');
  };

  const handleConfirmPredefined = async () => {
    if (!selectedGuest) return;
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/rsvp/${slug}/confirm`, {
        guest_name: selectedGuest.name,
        message: rsvpData.message || null
      });
      
      if (response.data.success) {
        setRsvpStep('success');
        toast.success('Presença confirmada!');
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao confirmar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRsvpSubmit = async (e) => {
    e.preventDefault();
    if (site?.is_demo) {
      toast.info('Modo demonstração: confirmação de presença desabilitada.');
      return;
    }

    
    if (rsvpMode === 'predefined') {
      if (rsvpStep === 'name') {
        handleSearchGuest();
        return;
      }
      return;
    }
    
    // Open mode
    setSubmitting(true);
    try {
      await axios.post(`${API}/rsvp/${slug}`, {
        guest_name: rsvpData.guest_name,
        guest_email: rsvpData.guest_email || null,
        guests_count: 1,
        attending: rsvpData.attending,
        message: rsvpData.message || null
      });
      toast.success('Presença confirmada com sucesso!');
      setShowRsvpModal(false);
      setRsvpData({ guest_name: '', guest_email: '', guests_count: 1, attending: true, message: '' });
    } catch (error) {
      toast.error('Erro ao confirmar presença');
    } finally {
      setSubmitting(false);
    }
  };
  
  const resetRsvpModal = () => {
    setShowRsvpModal(false);
    setRsvpStep('name');
    setSearchResults([]);
    setSelectedGuest(null);
    setRsvpData({ guest_name: '', guest_email: '', guests_count: 1, attending: true, message: '' });
  };

  const openGiftModal = async (gift) => {
    if (isTemplatePreview) {
      handleTemplatePreviewRedirect();
      return;
    }

    setSelectedGift(gift);
    setPaymentData({ payer_name: '', payer_email: '', payer_phone: '', payment_method: 'pix', doc_type: 'CPF', doc_number: '' });
    setPixPayment(null);
    setCardPayment(null);
    setCardFormData({
      cardNumber: '',
      cardExpiry: '',
      cardCvv: '',
      cardholderName: '',
      cardholderPhone: '',
      docType: 'CPF',
      docNumber: '',
      postalCode: '',
      addressNumber: '',
      addressComplement: '',
      addressStreet: '',
      addressNeighborhood: '',
      addressCity: '',
      addressState: ''
    });
    setPostalCodeLookup({ status: 'idle', message: '' });
    setEditingCardAddress(false);
    setPaymentStep('info');
    setSelectedInstallment(1);
    setCardCheckoutMode(null);
    setCardFeeResponsibility(site?.card_fee_responsibility === 'couple' ? 'couple' : 'payer');
    setCardEnabledForGift(null);
    setCardUnavailableReason('');
    setGiftCheckoutStatus({ loading: true, available: false, maintenance: false });
    setShowGiftModal(true);

    try {
      const availability = await axios.get(`${API}/payments/checkout-availability`);
      const available = availability.data?.available === true;
      setGiftCheckoutStatus({
        loading: false,
        available,
        maintenance: availability.data?.maintenance === true,
      });
      if (!available) return;
    } catch (error) {
      setGiftCheckoutStatus({ loading: false, available: false, maintenance: true });
      return;
    }
    
    // Fetch installment options
    try {
      const response = await axios.get(`${API}/payments/installments/${gift.gift_id}`);
      setInstallments(response.data.installments || []);
      if (response.data?.card_checkout_mode) {
        setCardCheckoutMode(response.data.card_checkout_mode);
      }
      if (response.data?.card_fee_responsibility) {
        setCardFeeResponsibility(response.data.card_fee_responsibility === 'couple' ? 'couple' : 'payer');
      }
      if (typeof response.data?.card_enabled === 'boolean') {
        setCardEnabledForGift(response.data.card_enabled);
      }
      if (response.data?.card_unavailable_reason) {
        setCardUnavailableReason(response.data.card_unavailable_reason);
      }
    } catch (error) {
      console.error('Error fetching installments:', error);
      setInstallments([]);
      setCardEnabledForGift(fallbackCardEnabled);
    }
  };

  const handlePaymentMethodSelect = (method) => {
    if (method === 'card' && !cardPaymentEnabled) {
      toast.error(cardUnavailableReason || 'Pagamento com cartão indisponível no momento.');
      return;
    }
    setPaymentData(prev => ({ ...prev, payment_method: method }));
  };

  const startHostedCardCheckout = async () => {
    trackEvent('checkout_started', {
      payment_method: 'card_hosted',
      gift_id: selectedGift?.gift_id,
      gift_name: selectedGift?.name,
      value: selectedGift?.price,
      slug,
      installments: selectedInstallment,
    });
    const response = await axios.post(`${API}/payments/card/checkout`, {
      gift_id: selectedGift.gift_id,
      payer_name: paymentData.payer_name,
      payer_email: paymentData.payer_email,
      doc_type: paymentData.doc_type,
      doc_number: paymentData.doc_number,
      installments: selectedInstallment,
    });

    setCardPayment({
      status: 'pending',
      ...response.data,
    });
    setPaymentStep('pending');

    const checkoutUrl = response.data?.checkout_url;
    if (checkoutUrl) {
      const popup = window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
      if (!popup) {
        window.location.href = checkoutUrl;
      }
      toast.info('Checkout seguro aberto. Finalize o pagamento no provedor.');
    } else {
      toast.info('Pagamento criado. Abra o checkout seguro quando estiver pronto.');
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!giftCheckoutStatus.available) {
      toast.error('A compra de presentes está temporariamente indisponível. Tente novamente mais tarde.');
      return;
    }
    
    if (paymentStep === 'info') {
      const docDigits = (paymentData.doc_number || '').replace(/\D/g, '');
      if (paymentData.doc_type === 'CPF' && docDigits.length !== 11) {
        toast.error('Informe um CPF válido para continuar.');
        return;
      }
      if (paymentData.doc_type === 'CNPJ' && docDigits.length !== 14) {
        toast.error('Informe um CNPJ válido para continuar.');
        return;
      }
      setCardFormData((prev) => ({
        ...prev,
        docType: paymentData.doc_type || 'CPF',
        docNumber: paymentData.doc_number || ''
      }));
      // Move to payment method selection
      setPaymentStep('method');
      return;
    }
    
    setSubmitting(true);
    try {
      if (paymentData.payment_method === 'pix') {
        if (GIFT_PAYMENT_PROVIDER === 'pagarme' && (
          cardPostalCodeDigits.length !== 8 ||
          !cardFormData.addressNumber.trim() ||
          !hasCompleteCardAddress
        )) {
          toast.error('Informe o endereço completo do comprador para pagar via PIX.');
          return;
        }
        trackEvent('checkout_started', {
          payment_method: 'pix',
          gift_id: selectedGift?.gift_id,
          gift_name: selectedGift?.name,
          value: selectedGift?.price,
          slug,
        });
        // Create PIX payment
        const response = await axios.post(`${API}/payments/pix`, {
          gift_id: selectedGift.gift_id,
          payer_name: paymentData.payer_name,
          payer_email: paymentData.payer_email,
          payer_phone: paymentData.payer_phone,
          doc_type: paymentData.doc_type,
          doc_number: paymentData.doc_number,
          ...(GIFT_PAYMENT_PROVIDER === 'pagarme' ? {
            postal_code: cardFormData.postalCode,
            address_number: cardFormData.addressNumber.trim(),
            address_complement: cardFormData.addressComplement.trim(),
            address_street: cardFormData.addressStreet.trim(),
            address_neighborhood: cardFormData.addressNeighborhood.trim(),
            address_city: cardFormData.addressCity.trim(),
            address_state: cardFormData.addressState.trim(),
          } : {}),
        });
        
        setPixPayment(response.data);
        setPaymentStep('processing');
        if (response.data?.is_test_mode || response.data?.payment_environment === 'test') {
          toast.info('PIX gerado em modo teste. Para pagamento real, configure credenciais de produção do Mercado Pago.');
        } else {
          toast.success('QR Code PIX gerado! Escaneie para pagar.');
        }
      } else {
        if (cardUsesHostedCheckout) {
          await startHostedCardCheckout();
        } else {
          // Show card form
          setPaymentStep('card_form');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar pagamento');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCardPaymentSubmit = async (formData) => {
    if (!giftCheckoutStatus.available) {
      toast.error('A compra de presentes está temporariamente indisponível.');
      return;
    }
    setProcessingCard(true);
    try {
      trackEvent('checkout_started', {
        payment_method: 'card',
        gift_id: selectedGift?.gift_id,
        gift_name: selectedGift?.name,
        value: selectedGift?.price,
        slug,
        installments: formData.installments || 1,
      });
      const payload = {
        gift_id: selectedGift.gift_id,
        payer_name: paymentData.payer_name,
        payer_email: paymentData.payer_email,
        card_token: formData.token,
        installments: formData.installments || 1,
        payment_method_id: formData.payment_method_id,
        issuer_id: formData.issuer_id,
        doc_type: formData.doc_type,
        doc_number: formData.doc_number
      };

      if (GIFT_PAYMENT_PROVIDER === 'asaas' && !CARD_FLOW_TOKEN_ONLY) {
        payload.card_number = formData.card_number;
        payload.card_expiry_month = formData.card_expiry_month;
        payload.card_expiry_year = formData.card_expiry_year;
        payload.card_cvv = formData.card_cvv;
        payload.cardholder_name = formData.cardholder_name;
        payload.cardholder_phone = formData.cardholder_phone;
        payload.postal_code = formData.postal_code;
        payload.address_number = formData.address_number;
        payload.address_complement = formData.address_complement;
      }

      if (GIFT_PAYMENT_PROVIDER === 'pagarme') {
        payload.cardholder_phone = formData.cardholder_phone;
        payload.postal_code = formData.postal_code;
        payload.address_number = formData.address_number;
        payload.address_complement = formData.address_complement;
        payload.address_street = formData.address_street;
        payload.address_neighborhood = formData.address_neighborhood;
        payload.address_city = formData.address_city;
        payload.address_state = formData.address_state;
      }

      const response = await axios.post(`${API}/payments/card`, payload);

      const normalizedStatus = (response.data.status || '').toLowerCase();
      if (normalizedStatus === 'approved') {
        setCardPayment({ status: 'approved', ...response.data });
        setPaymentStep('success');
        trackEvent('purchase_completed', {
          payment_method: 'card',
          gift_id: selectedGift?.gift_id,
          gift_name: selectedGift?.name,
          value: selectedGift?.price,
          slug,
        });
        toast.success('Pagamento aprovado! Obrigado pelo presente!');
        fetchSite();
      } else if (normalizedStatus === 'in_process' || normalizedStatus === 'pending') {
        setCardPayment({ status: 'pending', ...response.data });
        setPaymentStep('pending');
        toast.info('Pagamento em análise. Você receberá uma confirmação em breve.');
      } else {
        setCardPayment({ status: 'rejected', ...response.data });
        toast.error(`Pagamento não aprovado: ${response.data.status_detail || 'Tente outro cartão'}`);
      }
    } catch (error) {
      console.error('Card payment error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar pagamento com cartão');
    } finally {
      setProcessingCard(false);
    }
  };

  // Process card form manually
  const processCardForm = async (e) => {
    e.preventDefault();
    if (!giftCheckoutStatus.available) {
      toast.error('A compra de presentes está temporariamente indisponível.');
      return;
    }
    setCardError('');
    setProcessingCard(true);
    
    try {
      const formValues = new FormData(e.currentTarget);
      const effectiveCardFormData = {
        cardNumber: String(formValues.get('card_number') || cardFormData.cardNumber || '').trim(),
        cardExpiry: String(formValues.get('card_expiry') || cardFormData.cardExpiry || '').trim(),
        cardCvv: String(formValues.get('card_cvv') || cardFormData.cardCvv || '').trim(),
        cardholderName: String(formValues.get('cardholder_name') || cardFormData.cardholderName || '').trim(),
        cardholderPhone: String(formValues.get('cardholder_phone') || cardFormData.cardholderPhone || '').trim(),
        docType: String(formValues.get('doc_type') || cardFormData.docType || 'CPF').trim() || 'CPF',
        docNumber: String(formValues.get('doc_number') || cardFormData.docNumber || '').trim(),
        postalCode: String(formValues.get('postal_code') || cardFormData.postalCode || '').trim(),
        addressNumber: String(formValues.get('address_number') || cardFormData.addressNumber || '').trim(),
        addressComplement: String(formValues.get('address_complement') || cardFormData.addressComplement || '').trim(),
        addressStreet: String(formValues.get('address_street') || cardFormData.addressStreet || '').trim(),
        addressNeighborhood: String(formValues.get('address_neighborhood') || cardFormData.addressNeighborhood || '').trim(),
        addressCity: String(formValues.get('address_city') || cardFormData.addressCity || '').trim(),
        addressState: String(formValues.get('address_state') || cardFormData.addressState || '').trim(),
      };

      // validate form fields
      if (
        !effectiveCardFormData.cardNumber ||
        !effectiveCardFormData.cardExpiry ||
        !effectiveCardFormData.cardCvv ||
        !effectiveCardFormData.cardholderName ||
        !effectiveCardFormData.cardholderPhone ||
        !effectiveCardFormData.docNumber ||
        !effectiveCardFormData.postalCode ||
        !effectiveCardFormData.addressNumber
      ) {
        throw new Error('Preencha todos os campos do cartão, incluindo celular, CEP e número do endereço.');
      }
      if (
        GIFT_PAYMENT_PROVIDER === 'pagarme' &&
        (!effectiveCardFormData.addressStreet ||
          !effectiveCardFormData.addressNeighborhood ||
          !effectiveCardFormData.addressCity ||
          effectiveCardFormData.addressState.length !== 2)
      ) {
        throw new Error('Preencha rua, bairro, cidade e UF do titular do cartão.');
      }

      const cardholderPhoneDigits = effectiveCardFormData.cardholderPhone.replace(/\D/g, '');
      if (![10, 11].includes(cardholderPhoneDigits.length)) {
        throw new Error('Informe um celular válido do titular com DDD.');
      }

      // Parse expiry date
      const [expiryMonth, expiryYearRaw] = effectiveCardFormData.cardExpiry.split('/').map(s => s.trim());
      if (!expiryMonth || !expiryYearRaw) {
        throw new Error('Data de validade invalida (MM/AA)');
      }
      const expiryYear = expiryYearRaw.length === 2 ? `20${expiryYearRaw}` : expiryYearRaw;

      if (GIFT_PAYMENT_PROVIDER === 'asaas') {
        if (CARD_FLOW_TOKEN_ONLY) {
          throw new Error('Cartao em atualizacao de seguranca. Use PIX no momento.');
        }
        await handleCardPaymentSubmit({
          installments: selectedInstallment,
          payment_method_id: 'credit_card',
          card_number: effectiveCardFormData.cardNumber.replace(/\s/g, ''),
          card_expiry_month: expiryMonth,
          card_expiry_year: expiryYear,
          card_cvv: effectiveCardFormData.cardCvv,
          cardholder_name: effectiveCardFormData.cardholderName.trim(),
          cardholder_phone: cardholderPhoneDigits,
          doc_type: effectiveCardFormData.docType,
          doc_number: effectiveCardFormData.docNumber.replace(/\D/g, ''),
          postal_code: effectiveCardFormData.postalCode.replace(/\D/g, ''),
          address_number: effectiveCardFormData.addressNumber.trim(),
          address_complement: effectiveCardFormData.addressComplement.trim()
        });
        return;
      }

      if (GIFT_PAYMENT_PROVIDER === 'pagarme') {
        if (!PAGARME_PUBLIC_KEY) {
          throw new Error('Pagamento com cartão indisponível no momento.');
        }
        const tokenResponse = await fetch(
          `https://api.pagar.me/core/v5/tokens?appId=${encodeURIComponent(PAGARME_PUBLIC_KEY)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'card',
              card: {
                number: effectiveCardFormData.cardNumber.replace(/\D/g, ''),
                holder_name: effectiveCardFormData.cardholderName.trim(),
                holder_document: effectiveCardFormData.docNumber.replace(/\D/g, ''),
                exp_month: Number(expiryMonth),
                exp_year: Number(expiryYear),
                cvv: effectiveCardFormData.cardCvv,
              },
            }),
          }
        );
        const tokenData = await tokenResponse.json().catch(() => ({}));
        if (!tokenResponse.ok || !tokenData?.id) {
          const firstError = Array.isArray(tokenData?.errors) ? tokenData.errors[0] : null;
          const tokenError = firstError?.message || firstError?.description || tokenData?.message;
          throw new Error(tokenError || 'Não foi possível validar o cartão. Verifique os dados.');
        }

        await handleCardPaymentSubmit({
          token: tokenData.id,
          installments: selectedInstallment,
          payment_method_id: tokenData.card?.brand || 'credit_card',
          cardholder_phone: cardholderPhoneDigits,
          doc_type: effectiveCardFormData.docType,
          doc_number: effectiveCardFormData.docNumber.replace(/\D/g, ''),
          postal_code: effectiveCardFormData.postalCode.replace(/\D/g, ''),
          address_number: effectiveCardFormData.addressNumber.trim(),
          address_complement: effectiveCardFormData.addressComplement.trim(),
          address_street: effectiveCardFormData.addressStreet.trim(),
          address_neighborhood: effectiveCardFormData.addressNeighborhood.trim(),
          address_city: effectiveCardFormData.addressCity.trim(),
          address_state: effectiveCardFormData.addressState.trim().toUpperCase(),
        });
        return;
      }

      if (!mpInstance) {
        throw new Error('SDK do Mercado Pago não carregada');
      }

      // Create card token using MercadoPago SDK
      const cardToken = await mpInstance.createCardToken({
        cardNumber: effectiveCardFormData.cardNumber.replace(/\s/g, ''),
        cardholderName: effectiveCardFormData.cardholderName.toUpperCase(),
        cardExpirationMonth: expiryMonth,
        cardExpirationYear: expiryYear,
        securityCode: effectiveCardFormData.cardCvv,
        identificationType: effectiveCardFormData.docType,
        identificationNumber: effectiveCardFormData.docNumber.replace(/\D/g, '')
      });

      if (!cardToken?.id) {
        throw new Error('Não foi possível tokenizar o cartão. Verifique os dados.');
      }

      // Get card payment method from BIN
      const bin = effectiveCardFormData.cardNumber.replace(/\s/g, '').substring(0, 6);
      const paymentMethods = await mpInstance.getPaymentMethods({ bin });
      const paymentMethod = paymentMethods.results?.[0];

      // Get issuer
      const issuers = await mpInstance.getIssuers({ paymentMethodId: paymentMethod?.id, bin });
      const issuer = issuers?.[0];

      // Submit payment to backend
      await handleCardPaymentSubmit({
        token: cardToken.id,
        installments: selectedInstallment,
        payment_method_id: paymentMethod?.id || 'visa',
        issuer_id: issuer?.id || null,
        doc_type: effectiveCardFormData.docType,
        doc_number: effectiveCardFormData.docNumber.replace(/\D/g, '')
      });
    } catch (error) {
      console.error('Card form error:', error);
      setCardError(error.message || 'Erro ao processar o cartão. Verifique os dados.');
      toast.error(error.message || 'Erro ao processar pagamento');
    } finally {
      setProcessingCard(false);
    }
  };

  // Format card number with spaces
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  // Format expiry date
  const formatExpiry = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  // Format CPF/CNPJ
  const formatDocument = (value, docType = 'CPF') => {
    const v = value.replace(/\D/g, '');
    if (docType === 'CNPJ') {
      return v
        .substring(0, 14)
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return v
      .substring(0, 11)
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1-$2');
  };

  const formatPostalCode = (value) => {
    const v = value.replace(/\D/g, '').substring(0, 8);
    if (v.length <= 5) {
      return v;
    }
    return `${v.substring(0, 5)}-${v.substring(5)}`;
  };

  const handlePostalCodeChange = (value) => {
    const postalCode = formatPostalCode(value);
    setCardFormData(prev => ({
      ...prev,
      postalCode,
      addressStreet: '',
      addressNeighborhood: '',
      addressCity: '',
      addressState: '',
    }));
    setPostalCodeLookup({ status: 'idle', message: '' });
    setEditingCardAddress(false);
  };

  const formatCardholderPhone = (value) => {
    const digits = value.replace(/\D/g, '').substring(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 6) return `(${digits.substring(0, 2)}) ${digits.substring(2)}`;
    if (digits.length <= 10) {
      return `(${digits.substring(0, 2)}) ${digits.substring(2, 6)}-${digits.substring(6)}`;
    }
    return `(${digits.substring(0, 2)}) ${digits.substring(2, 7)}-${digits.substring(7)}`;
  };

  const handleCardPaymentError = (error) => {
    console.error('Card form error:', error);
    toast.error('Erro no formulário do cartão. Verifique os dados.');
  };

  const copyPixCode = () => {
    if (pixPayment?.qr_code_text) {
      navigator.clipboard.writeText(pixPayment.qr_code_text);
      toast.success('Código PIX copiado!');
    }
  };

  const checkPaymentStatus = async () => {
    if (!pixPayment?.payment_id) return;
    
    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/payments/${pixPayment.payment_id}/status`);
      if (response.data.status === 'approved') {
        trackEvent('purchase_completed', {
          payment_method: 'pix',
          gift_id: selectedGift?.gift_id,
          gift_name: selectedGift?.name,
          value: selectedGift?.price,
          slug,
        });
        toast.success('Pagamento confirmado! Obrigado pelo presente!');
        setShowGiftModal(false);
        setPixPayment(null);
        fetchSite();
      } else {
        toast.info('Pagamento ainda não confirmado. Aguarde alguns instantes.');
      }
    } catch (error) {
      toast.error('Erro ao verificar pagamento');
    } finally {
      setCheckingPayment(false);
    }
  };

  const closeGiftModal = () => {
    // Stop polling when closing modal
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setShowGiftModal(false);
    setPixPayment(null);
    setCardPayment(null);
    setSelectedGift(null);
    setPaymentStep('info');
    setCardFeeResponsibility(site?.card_fee_responsibility === 'couple' ? 'couple' : 'payer');
    setCardEnabledForGift(null);
    setCardUnavailableReason('');
  };

  const openGallery = (index) => {
    setGalleryIndex(index);
    setShowGalleryModal(true);
  };

  const handleTemplatePreviewRedirect = () => {
    navigate(previewRegisterHref);
  };

  const nextImage = () => {
    if (site?.gallery_images?.length) {
      setGalleryIndex((prev) => (prev + 1) % site.gallery_images.length);
    }
  };

  const prevImage = () => {
    if (site?.gallery_images?.length) {
      setGalleryIndex((prev) => (prev - 1 + site.gallery_images.length) % site.gallery_images.length);
    }
  };

  const getGoogleMapsUrl = (address) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const getGoogleMapsEmbed = (address) => {
    return `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(address)}`;
  };

  if (loading) {
    return (
      <>
        <SEOHead
          title={seoConfig.title}
          description={seoConfig.description}
          canonical={seoConfig.canonical}
          jsonLd={seoConfig.jsonLd}
          noindex={seoConfig.noindex}
        />
        <div className="min-h-screen flex items-center justify-center bg-[#F6F1EF]">
          <div className="text-center">
            <img src={brandMark} alt="Virei Noiva" className="w-16 h-16 mx-auto mb-6 animate-pulse object-contain" />
            <div className="w-12 h-12 border-4 border-[#E8B7B1] border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SEOHead
          title={seoConfig.title}
          description={seoConfig.description}
          canonical={seoConfig.canonical}
          jsonLd={seoConfig.jsonLd}
          noindex={seoConfig.noindex}
        />
        <div className="min-h-screen flex items-center justify-center bg-[#F6F1EF]">
          <div className="text-center">
            <img src={brandMark} alt="Virei Noiva" className="w-16 h-16 mx-auto mb-6 object-contain" />
            <h1 className="text-2xl font-bold text-[#1C1C1C] mb-2">{error}</h1>
            <p className="text-[#333333]">O site que você está procurando não existe ou não está publicado.</p>
          </div>
        </div>
      </>
    );
  }

  const weddingDate = new Date(site.wedding_date);
  const daysUntil = Math.max(0, Math.ceil((weddingDate - new Date()) / (1000 * 60 * 60 * 24)));

  const resolvedTemplateId = resolveTemplateId(templateId || site.template_id || 'ivory-classic');
  const templateProfile = TEMPLATE_RENDER_PROFILES[resolvedTemplateId] || TEMPLATE_RENDER_PROFILES['ivory-classic'];
  const isClassicEditorialTemplate = resolvedTemplateId === 'classic-editorial';
  const isBotanicalGardenTemplate = resolvedTemplateId === 'botanical-garden';
  const isModernBlackTieTemplate = resolvedTemplateId === 'modern-black-tie';
  const isDestinationPraiaTemplate = resolvedTemplateId === 'destination-praia';
  const isMinimalClassicTemplate = resolvedTemplateId === 'minimal-classic';
  const templateTheme = TEMPLATE_THEME_MAP[resolvedTemplateId];
  const selectedTemplateMeta = TEMPLATE_LOOKUP[resolvedTemplateId];
  const primaryColor = normalizeHexColor(site.primary_color) || templateTheme?.primary || '#1C1C1C';
  const secondaryColor = normalizeHexColor(site.secondary_color) || templateTheme?.secondary || '#C8A97E';
  const hasCustomTextColor = Boolean(site.text_color);
  const bodyTextColor = hasCustomTextColor ? site.text_color : '#333333';
  const bodyTextOnDark = hasCustomTextColor ? site.text_color : '#E2E8F0';
  const bodyTextOnPrimary = hasCustomTextColor ? site.text_color : 'rgba(255,255,255,0.85)';
  const headingFontFamily = FONT_FAMILY_BY_ID[site.heading_font] || "'Playfair Display', serif";
  const bannerTitleFontFamily = FONT_FAMILY_BY_ID[site.banner_title_font_family] || headingFontFamily;
  const storyTitleFontFamily = FONT_FAMILY_BY_ID[site.story_title_font_family] || headingFontFamily;
  const bodyFontFamily = FONT_FAMILY_BY_ID[site.body_font] || "'Montserrat', sans-serif";
  const bannerTextFontFamily = FONT_FAMILY_BY_ID[site.banner_text_font_family] || bodyFontFamily;
  const storyBodyFontFamily = FONT_FAMILY_BY_ID[site.story_text_font_family] || bodyFontFamily;
  const locationTitleFontFamily = FONT_FAMILY_BY_ID[site.location_title_font_family] || headingFontFamily;
  const locationTextFontFamily = FONT_FAMILY_BY_ID[site.location_text_font_family] || bodyFontFamily;
  const rsvpTitleFontFamily = FONT_FAMILY_BY_ID[site.rsvp_title_font_family] || headingFontFamily;
  const rsvpTextFontFamily = FONT_FAMILY_BY_ID[site.rsvp_text_font_family] || bodyFontFamily;
  const isDarkTemplate = ['obsidian', 'midnight', 'digital'].includes(templateProfile.sectionTone);
  const bannerTextColor = normalizeHexColor(site.banner_text_color) || null;
  const bannerSectionBackgroundColor = normalizeHexColor(site.banner_section_background_color) || null;
  const giftTitleColor = normalizeHexColor(site.gift_title_color) || (isDarkTemplate ? '#F8FAFC' : primaryColor);
  const giftTextColor = normalizeHexColor(site.gift_text_color) || (isDarkTemplate ? bodyTextOnDark : bodyTextColor);
  const giftIconColor = normalizeHexColor(site.gift_icon_color) || secondaryColor;
  const giftSectionBackgroundColor = normalizeHexColor(site.gift_section_background_color) || (isDarkTemplate ? '#0B1220' : '#FFFFFF');
  const giftCardBackgroundColor = normalizeHexColor(site.gift_card_background_color) || (isDarkTemplate ? '#F6F1EF99' : '#FFFFFF');
  const giftPriceColor = normalizeHexColor(site.gift_price_color) || secondaryColor;
  const giftButtonBackgroundColor = normalizeHexColor(site.gift_button_background_color) || primaryColor;
  const giftButtonTextColor = normalizeHexColor(site.gift_button_text_color) || getAutoContrastTextColor(giftButtonBackgroundColor, '#FFFFFF');
  const locationTitleColor = normalizeHexColor(site.location_title_color) || (templateProfile.locationLayout === 'glow' ? '#F8FAFC' : primaryColor);
  const locationTextColor = normalizeHexColor(site.location_text_color) || (templateProfile.locationLayout === 'glow' ? bodyTextOnDark : bodyTextColor);
  const storySectionBackgroundColor = normalizeHexColor(site.story_section_background_color) || null;
  const locationSectionBackgroundColor = normalizeHexColor(site.location_section_background_color) || null;
  const locationCardBackgroundColor = normalizeHexColor(site.location_card_background_color) || null;
  const locationIconColor = normalizeHexColor(site.location_icon_color) || null;
  const rsvpSectionBackgroundColor = normalizeHexColor(site.rsvp_section_background_color) || null;
  const rsvpTextColor = normalizeHexColor(site.rsvp_text_color) || null;
  const rsvpIconColor = normalizeHexColor(site.rsvp_icon_color) || null;
  const templatePreviewImage = TEMPLATE_LOOKUP[resolvedTemplateId]?.preview_image;
  const heroImageSrc = getSafeImageSrc(site.hero_image || templatePreviewImage || FALLBACK_HERO_IMAGE);
  const heroOverlayOpacity = Math.max(0, Math.min(100, Number(site.hero_overlay_opacity ?? 70))) / 100;
  const isPremiumWeddingTemplate = resolvedTemplateId === 'premium-wedding-experience';
  const isHtmlExactTemplate = resolvedTemplateId === 'ethereal-grid';
  const htmlExactDateLabel = weddingDate.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
  const htmlExactPlaceLabel = (
    site.ceremony_location ||
    site.reception_location ||
    site.ceremony_address ||
    site.reception_address ||
    ''
  ).trim();

  const sectionBackgroundMap = {
    paper: '#F6F1EF',
    cream: '#F9F4EE',
    concrete: '#E6E6E6',
    obsidian: '#0E1117',
    warm: '#F5E9DF',
    sea: '#F0F7FB',
    forest: '#E7EFE8',
    petal: '#F6EEF0',
    royal: '#F1F4FA',
    ivory: '#FAF8F2',
    lace: '#F8F3EF',
    midnight: '#11131B',
    retro: '#FFF8DF',
    pastel: '#F8FBF8',
    storybook: '#FFF7F2',
    contrast: '#F5F5F5',
    digital: '#EDF3FA',
    travel: '#F8F4ED',
    botanical: '#F4F8F5',
    watercolor: '#F8F7FF'
  };
  const sectionBackground = sectionBackgroundMap[templateProfile.sectionTone] || '#F6F1EF';
  const galleryTitleColor = normalizeHexColor(site.gallery_title_color) || (isDarkTemplate ? '#F8FAFC' : primaryColor);
  const galleryIconColor = normalizeHexColor(site.gallery_icon_color) || secondaryColor;
  const gallerySectionBackgroundColor = normalizeHexColor(site.gallery_section_background_color) || (isDarkTemplate ? '#0F172A' : '#FFFFFF');

  const availableGifts = site.gifts?.filter(g => g.is_available) || [];
  const galleryPosition = ['after_hero', 'after_story', 'before_gifts', 'end'].includes(site.gallery_position)
    ? site.gallery_position
    : 'after_story';
  const galleryCardClass =
    templateProfile.storyLayout === 'polaroid' ? 'rounded-sm border-4 border-white shadow-md' :
    templateProfile.storyLayout === 'blocks' ? 'rounded-none border border-slate-300' :
    templateProfile.storyLayout === 'clouds' ? 'rounded-[2rem]' :
    'rounded-xl';

  const gallerySection = site.gallery_images && site.gallery_images.length > 0 ? (
    <section className="py-20 px-6" style={{ background: gallerySectionBackgroundColor }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <Camera className="w-12 h-12 mx-auto mb-4" style={{ color: galleryIconColor }} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: headingFontFamily, color: galleryTitleColor }}>
            Nossa Galeria
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {site.gallery_images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => openGallery(idx)}
              className={`aspect-square overflow-hidden hover:opacity-90 transition-opacity ${galleryCardClass}`}
            >
              <img
                src={img}
                alt={`Gallery ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  ) : null;

  const getHeroStyles = () => {
    if (isHtmlExactTemplate) {
      return {
        overlay: 'linear-gradient(180deg, rgba(224,225,228,0.42) 0%, rgba(225,221,216,0.32) 55%, rgba(248,248,248,0.18) 100%)',
        textAlign: 'center',
        darkText: false,
        contentClass: 'px-6 max-w-4xl',
        shellClass: 'justify-center'
      };
    }

    switch (templateProfile.heroLayout) {
      case 'split':
        return {
          overlay: `linear-gradient(120deg, ${primaryColor}f0 0%, ${primaryColor}a6 50%, transparent 100%)`,
          textAlign: 'left',
          darkText: false,
          contentClass: 'w-full max-w-6xl mx-auto px-6 md:px-10',
          shellClass: 'justify-start'
        };
      case 'editorial':
        return {
          overlay: `linear-gradient(160deg, ${primaryColor}c7 0%, rgba(255,255,255,0.2) 100%)`,
          textAlign: 'left',
          darkText: false,
          contentClass: 'w-full max-w-6xl mx-auto px-6 md:px-10',
          shellClass: 'justify-center'
        };
      case 'full':
        return {
          overlay: `linear-gradient(180deg, ${primaryColor}d9 0%, ${primaryColor}b3 60%, ${primaryColor}ee 100%)`,
          textAlign: 'center',
          darkText: false,
          contentClass: 'px-6 max-w-5xl',
          shellClass: 'justify-center'
        };
      case 'minimal':
      case 'airy':
        return {
          overlay: 'rgba(255,255,255,0.75)',
          textAlign: 'center',
          darkText: true,
          contentClass: 'px-6 max-w-4xl',
          shellClass: 'justify-center'
        };
      case 'premium-showcase':
        return {
          overlay: 'linear-gradient(180deg, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.34) 55%, rgba(255,255,255,0.12) 100%)',
          textAlign: 'center',
          darkText: false,
          contentClass: 'px-6 max-w-4xl',
          shellClass: 'justify-center'
        };
      case 'panoramic':
        return {
          overlay: `linear-gradient(180deg, transparent 20%, ${primaryColor}d6 100%)`,
          textAlign: 'left',
          darkText: false,
          contentClass: 'w-full max-w-6xl mx-auto px-6 md:px-10',
          shellClass: 'justify-end'
        };
      case 'framed':
        return {
          overlay: `linear-gradient(180deg, ${primaryColor}99 0%, ${primaryColor}cc 100%)`,
          textAlign: 'center',
          darkText: false,
          contentClass: 'px-6 max-w-4xl',
          shellClass: 'justify-center',
          frame: true
        };
      case 'arch':
        return {
          overlay: `linear-gradient(180deg, ${primaryColor}88 0%, ${primaryColor}cc 100%)`,
          textAlign: 'center',
          darkText: false,
          contentClass: 'px-6 max-w-4xl',
          shellClass: 'justify-center',
          arch: true
        };
      default:
        return {
          overlay: `linear-gradient(to bottom, ${primaryColor}99, ${primaryColor}cc)`,
          textAlign: 'center',
          darkText: false,
          contentClass: 'px-6 max-w-4xl',
          shellClass: 'justify-center'
        };
    }
  };

  const heroStyles = getHeroStyles();
  const fallbackHeroHeadingFont = isHtmlExactTemplate
    ? 'Montserrat, system-ui, sans-serif'
    : isPremiumWeddingTemplate
    ? 'Montserrat, system-ui, sans-serif'
    : ['brutalist-chic', 'digital-dream', 'geometric-punch'].includes(resolvedTemplateId)
    ? 'Space Grotesk, system-ui, sans-serif'
    : ['boho-pampas', 'dried-floral-arch', 'watercolor-bloom'].includes(resolvedTemplateId)
      ? 'Playfair Display'
    : ['asymmetric-flow', 'vibrant-retro', 'pastel-love'].includes(resolvedTemplateId)
        ? 'Montserrat, system-ui, sans-serif'
        : 'Playfair Display';
  const heroHeadingFont = bannerTitleFontFamily || fallbackHeroHeadingFont;
  const heroNameClass =
    isHtmlExactTemplate ? 'text-[clamp(2.2rem,6vw,4.8rem)] font-light tracking-[0.07em] uppercase' :
    templateProfile.heroLayout === 'premium-showcase' ? 'text-4xl md:text-6xl lg:text-7xl tracking-[0.14em] uppercase font-light' :
    templateProfile.heroLayout === 'minimal' ? 'text-5xl md:text-8xl tracking-tight' :
    templateProfile.heroLayout === 'editorial' ? 'text-5xl md:text-7xl lg:text-8xl leading-[0.95]' :
    templateProfile.heroLayout === 'split' ? 'text-5xl md:text-6xl lg:text-7xl' :
    'text-5xl md:text-7xl';
  const heroDateClass =
    isHtmlExactTemplate ? 'text-lg md:text-3xl font-light tracking-[0.04em]' :
    templateProfile.heroLayout === 'premium-showcase' ? 'text-sm md:text-base uppercase tracking-[0.28em]' :
    templateProfile.countdownStyle === 'weight' ? 'text-lg md:text-2xl font-light tracking-[0.25em] uppercase' :
    templateProfile.countdownStyle === 'micro' ? 'text-sm md:text-base uppercase tracking-[0.35em]' :
    'text-xl md:text-2xl';
  const showHeroIcon = !isHtmlExactTemplate && !['minimal', 'airy', 'premium-showcase'].includes(templateProfile.heroLayout);

  const renderHeroCountdown = () => {
    if (templateProfile.countdownStyle === 'inline') {
      return (
        <p className="text-5xl md:text-6xl font-light mb-12 tracking-tight" style={{ color: heroStyles.darkText ? primaryColor : 'white' }}>
          {daysUntil} <span className="text-xl md:text-2xl">dias</span>
        </p>
      );
    }

    if (templateProfile.countdownStyle === 'ticker' || templateProfile.countdownStyle === 'matrix') {
      return (
        <div className="mb-12 overflow-hidden border-y border-white/30 py-3">
          <p className="whitespace-nowrap text-xl md:text-2xl font-bold tracking-[0.2em] uppercase animate-pulse" style={{ color: secondaryColor }}>
            {`${daysUntil} dias - ${daysUntil} dias - ${daysUntil} dias - ${daysUntil} dias`}
          </p>
        </div>
      );
    }

    if (templateProfile.countdownStyle === 'floating') {
      return (
        <div className="mb-12 text-center">
          <span className="text-7xl md:text-8xl font-extralight" style={{ color: secondaryColor }}>{daysUntil}</span>
          <p className={`uppercase tracking-[0.4em] text-xs mt-2 ${heroStyles.darkText ? 'text-[#333333]' : 'text-white/70'}`}>dias</p>
        </div>
      );
    }

    return (
      <div className="flex justify-center gap-4 md:gap-8 mb-12">
        <div className="text-center">
          <div
            className={`w-20 h-20 md:w-24 md:h-24 flex items-center justify-center mb-2 ${templateProfile.giftStyle === 'sharp' ? 'rounded-none border-2 border-white/30' : templateProfile.giftStyle === 'glass' ? 'rounded-full border-2' : 'rounded-2xl'}`}
            style={{
              background: `${secondaryColor}30`,
              borderColor: templateProfile.giftStyle === 'glass' ? secondaryColor : undefined
            }}
          >
            <span className="text-3xl md:text-4xl font-bold" style={{ color: secondaryColor }}>
              {daysUntil}
            </span>
          </div>
          <span className={`text-sm uppercase tracking-wider ${heroStyles.darkText ? 'text-[#333333]' : 'text-white/70'}`}>
            dias
          </span>
        </div>
      </div>
    );
  };

  const storySectionClass =
    isMinimalClassicTemplate ? 'rounded-none border-0' :
    isClassicEditorialTemplate ? 'rounded-3xl border border-[#E8DCC8] bg-[#FFFDF9] shadow-sm' :
    templateProfile.storyLayout === 'blocks' ? 'rounded-none border-y border-slate-300' :
    templateProfile.storyLayout === 'polaroid' ? 'rounded-3xl border border-white/50 shadow-xl' :
    templateProfile.storyLayout === 'timeline' ? 'rounded-2xl border border-white/20' :
    'rounded-2xl';

  const locationGridClass = templateProfile.locationLayout === 'split-map' ? 'grid md:grid-cols-1 lg:grid-cols-2 gap-10' : 'grid md:grid-cols-2 gap-8';

  const locationCardClass =
    isDestinationPraiaTemplate ? 'bg-white/95 border border-[#D9E7EF] shadow-md' :
    templateProfile.locationLayout === 'glow' ? 'bg-[#111827] border border-white/10 shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_20px_60px_rgba(0,0,0,0.45)]' :
    templateProfile.locationLayout === 'paper' ? 'bg-[#FFFCF7] border border-[#E8DCC8] shadow-sm' :
    templateProfile.locationLayout === 'watercolor' ? 'bg-white/90 border border-white shadow-md backdrop-blur' :
    'bg-white shadow-sm';

  const ceremonyLocation = (site.ceremony_location || '').trim();
  const ceremonyAddress = (site.ceremony_address || '').trim();
  const ceremonyTime = (site.ceremony_time || '').trim();
  const receptionLocation = (site.reception_location || '').trim();
  const receptionAddress = (site.reception_address || '').trim();
  const receptionTime = (site.reception_time || '').trim();
  const inferredSameLocation = Boolean(
    ceremonyLocation &&
      receptionLocation &&
      ceremonyLocation.toLowerCase() === receptionLocation.toLowerCase() &&
      ceremonyAddress.toLowerCase() === receptionAddress.toLowerCase()
  );
  const sameLocation = Boolean(site.same_location || inferredSameLocation);

  const locationCards = sameLocation
    ? [
        {
          key: 'combined',
          title: 'Cerimônia e Recepção',
          icon: MapPin,
          iconBg: secondaryColor,
          location: ceremonyLocation || receptionLocation,
          address: ceremonyAddress || receptionAddress,
          time: ceremonyTime || receptionTime,
          mapTitle: 'Mapa Cerimônia e Recepção'
        }
      ]
    : [
        ceremonyLocation
          ? {
              key: 'ceremony',
              title: 'Cerimônia',
              icon: Heart,
              iconBg: primaryColor,
              location: ceremonyLocation,
              address: ceremonyAddress,
              time: ceremonyTime,
              mapTitle: 'Mapa Cerimônia'
            }
          : null,
        receptionLocation
          ? {
              key: 'reception',
              title: 'Recepção',
              icon: Users,
              iconBg: secondaryColor,
              location: receptionLocation,
              address: receptionAddress,
              time: receptionTime,
              mapTitle: 'Mapa Recepção'
            }
          : null
      ].filter(Boolean);

  const locationGridClassResolved =
    locationCards.length <= 1
      ? 'grid md:grid-cols-1 gap-8 max-w-3xl mx-auto'
      : locationGridClass;

  const giftCardClass =
    templateProfile.giftStyle === 'sharp' ? 'rounded-none border border-slate-300' :
    templateProfile.giftStyle === 'glass' ? 'rounded-2xl border border-white/20 bg-white/10 backdrop-blur' :
    templateProfile.giftStyle === 'paper' ? 'rounded-xl border border-[#E8DCC8] bg-[#FFF9F2]' :
    templateProfile.giftStyle === 'wood' ? 'rounded-lg border border-[#B89A78]' :
    templateProfile.giftStyle === 'confetti' ? 'rounded-3xl border border-pink-200' :
    templateProfile.giftStyle === 'ticket' ? 'rounded-xl border-2 border-dashed border-slate-300' :
    'rounded-2xl border border-slate-100';

  const rsvpSectionClass =
    templateProfile.rsvpStyle === 'glow' ? 'relative overflow-hidden' :
    templateProfile.rsvpStyle === 'floating' ? 'relative' :
    '';

  const rsvpButtonClass =
    templateProfile.rsvpStyle === 'outline' ? 'h-14 px-10 rounded-none border-2 bg-transparent' :
    templateProfile.rsvpStyle === 'ticket' ? 'h-14 px-10 rounded-lg border-2 border-dashed bg-transparent' :
    templateProfile.rsvpStyle === 'floating' ? 'h-14 px-10 rounded-full shadow-xl' :
    'h-14 px-10 rounded-full';

  const heroTitleFallbackColor = isHtmlExactTemplate ? '#F2F4F7' : heroStyles.darkText ? primaryColor : 'white';
  const heroTitleColor = normalizeHexColor(site.banner_title_color) || site.text_color || heroTitleFallbackColor;
  const confirmButtonBackgroundColor = normalizeHexColor(site.confirm_button_background_color) || primaryColor;
  const confirmButtonTextColor = normalizeHexColor(site.confirm_button_text_color) || getAutoContrastTextColor(confirmButtonBackgroundColor, heroStyles.darkText ? '#1A1A1A' : '#FFFFFF');
  const confirmButtonBorderColor = normalizeHexColor(site.confirm_button_border_color) || primaryColor;
  const heroButtonStyle =
    templateProfile.rsvpStyle === 'outline' || templateProfile.rsvpStyle === 'ticket'
      ? {
          background: confirmButtonBackgroundColor,
          color: confirmButtonTextColor,
          borderColor: confirmButtonBorderColor
        }
      : {
          background: confirmButtonBackgroundColor,
          color: confirmButtonTextColor,
          borderColor: confirmButtonBorderColor,
          borderWidth: 1,
          borderStyle: 'solid'
        };

  const useStructuredTemplate = isStructuredTemplateId(resolvedTemplateId);
  const structuredTheme = {
    primaryColor,
    secondaryColor,
    bodyTextColor,
    headingFont: headingFontFamily,
    bannerTitleFont: heroHeadingFont,
    bannerTextFont: bannerTextFontFamily,
    bannerTextColor: bannerTextColor || (isHtmlExactTemplate ? 'rgba(244,247,251,0.9)' : heroStyles.darkText ? bodyTextColor : bodyTextOnPrimary),
    bannerSectionBackground: bannerSectionBackgroundColor,
    storyTitleFont: storyTitleFontFamily,
    storyBodyFont: storyBodyFontFamily,
    locationTitleColor,
    locationTitleFont: locationTitleFontFamily,
    locationTextColor,
    locationTextFont: locationTextFontFamily,
    heroImage: heroImageSrc,
    heroOverlay: heroStyles.overlay,
    heroOverlayOpacity,
    heroTitleColor,
    heroDateColor: isHtmlExactTemplate ? 'rgba(244,247,251,0.9)' : heroStyles.darkText ? bodyTextColor : bodyTextOnPrimary,
      storyTitleColor: normalizeHexColor(site.story_title_color) || (isHtmlExactTemplate ? '#252322' : 'white'),
      storyBodyColor: normalizeHexColor(site.story_title_color) || (isHtmlExactTemplate ? bodyTextColor : bodyTextOnPrimary),
      storySectionBackground: storySectionBackgroundColor,
      galleryTitleColor,
      galleryIconColor,
      gallerySectionBackground: gallerySectionBackgroundColor,
      giftTitleColor,
      giftTextColor,
      giftIconColor,
      giftSectionBackground: giftSectionBackgroundColor,
      giftCardBackground: giftCardBackgroundColor,
      giftPriceColor,
      giftButtonBackground: giftButtonBackgroundColor,
      giftButtonText: giftButtonTextColor,
      locationSectionBackground: locationSectionBackgroundColor,
      locationCardBackground: locationCardBackgroundColor,
      locationIconColor: locationIconColor || locationTitleColor,
      rsvpSectionBackground: rsvpSectionBackgroundColor,
      rsvpTextColor: rsvpTextColor || bodyTextColor,
      rsvpTitleFont: rsvpTitleFontFamily,
      rsvpTextFont: rsvpTextFontFamily,
      rsvpIconColor: rsvpIconColor || rsvpTextColor || bodyTextColor,
      confirmButtonBackground: confirmButtonBackgroundColor,
      confirmButtonText: confirmButtonTextColor,
      confirmButtonBorder: confirmButtonBorderColor
    };

  const heroTextContent = (
    <>
      {showHeroIcon && (
        <img
          src={brandMark}
          alt="Virei Noiva"
          className={`${isHtmlExactTemplate ? 'w-10 h-10 opacity-95' : 'w-12 h-12'} ${heroStyles.textAlign === 'left' ? '' : 'mx-auto'} mb-6 object-contain`}
        />
      )}

      <h1
        className={`font-bold mb-6 ${heroNameClass}`}
        style={{
          fontFamily: heroHeadingFont,
          color: heroTitleColor,
          textShadow: isPremiumWeddingTemplate || isHtmlExactTemplate ? '0 2px 7px rgba(0,0,0,0.25)' : 'none'
        }}
      >
        {(isPremiumWeddingTemplate || isHtmlExactTemplate) ? String(site.couple_names || '').toUpperCase() : site.couple_names}
      </h1>

      <p
        className={`mb-8 ${heroDateClass}`}
        style={{ color: bannerTextColor || (isHtmlExactTemplate ? 'rgba(244,247,251,0.9)' : heroStyles.darkText ? bodyTextColor : bodyTextOnPrimary), fontFamily: bannerTextFontFamily }}
      >
        {isHtmlExactTemplate
          ? htmlExactDateLabel
          : weddingDate.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
      </p>

      {isModernBlackTieTemplate && (
        <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-white/25 bg-black/35 px-4 py-3 text-left">
            <p className="text-[0.65rem] uppercase tracking-[0.18em] text-white/60">Contagem</p>
            <p className="text-xl font-semibold text-white">{daysUntil} dias</p>
          </div>
          <div className="rounded-xl border border-white/25 bg-black/35 px-4 py-3 text-left">
            <p className="text-[0.65rem] uppercase tracking-[0.18em] text-white/60">Cerimônia</p>
            <p className="text-sm font-medium text-white">{site.ceremony_time || '--:--'}</p>
          </div>
          <div className="rounded-xl border border-white/25 bg-black/35 px-4 py-3 text-left">
            <p className="text-[0.65rem] uppercase tracking-[0.18em] text-white/60">Local</p>
            <p className="text-sm font-medium text-white truncate">{site.ceremony_location || site.reception_location || 'A confirmar'}</p>
          </div>
        </div>
      )}

      {!isPremiumWeddingTemplate && !isHtmlExactTemplate && renderHeroCountdown()}

      {!isPremiumWeddingTemplate && !isHtmlExactTemplate && (
        <Button
          onClick={() => (isTemplatePreview ? handleTemplatePreviewRedirect() : setShowRsvpModal(true))}
          className={`${rsvpButtonClass} font-bold text-lg transition-colors duration-300`}
          style={heroButtonStyle}
          data-testid="rsvp-btn"
        >
          {templateProfile.countdownStyle === 'inline' ? 'CONFIRMAR PRESEN\u00c7A' : 'Confirmar Presen\u00e7a'}
        </Button>
      )}
    </>
  );

  return (
    <>
      <SEOHead
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={seoConfig.canonical}
        jsonLd={seoConfig.jsonLd}
        noindex={seoConfig.noindex}
      />
      <div className={`min-h-screen ${isTemplatePreview ? 'pt-28 md:pt-32' : ''}`} style={{ background: sectionBackground }}>
      {isTemplatePreview && (
        <div className="fixed inset-x-0 top-3 z-[70] px-4">
          <div className="mx-auto flex max-w-6xl flex-col gap-4 rounded-[1.75rem] border border-white/60 bg-[#F6F1EF]/78 p-4 shadow-[0_24px_60px_-34px_rgba(28,28,28,0.45)] backdrop-blur-xl md:flex-row md:items-center md:justify-between md:p-5">
            <div className="min-w-0">
              <p className="text-[0.68rem] font-semibold uppercase tracking-[0.24em] text-[#C8A97E]">
                Prévia do template
              </p>
              <h2 className="mt-1 text-xl text-[#1C1C1C] md:text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                {selectedTemplateMeta?.label || selectedTemplateMeta?.name || 'Template selecionado'}
              </h2>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-[#4D4541]">
                Esta é a demonstração do estilo escolhido. Crie sua conta para publicar com esse template ou entre para editar agora.
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Link
                to={previewRegisterHref}
                className="inline-flex h-11 items-center justify-center rounded-full bg-[#E8B7B1] px-5 text-sm font-semibold text-white transition hover:bg-[#DFA8A1]"
              >
                Criar conta com este template
              </Link>
              <Link
                to={`/login?next=${encodeURIComponent(`/editor?template=${resolvedTemplateId}`)}`}
                className="inline-flex h-11 items-center justify-center rounded-full border border-[#E8B7B1]/55 bg-white/80 px-5 text-sm font-semibold text-[#B97D5B] transition hover:bg-[#F8EFEC]"
              >
                Já tenho conta
              </Link>
            </div>
          </div>
        </div>
      )}

      {useStructuredTemplate ? (
        <WeddingTemplateRenderer
          templateId={resolvedTemplateId}
          site={site}
          theme={structuredTheme}
          daysUntil={daysUntil}
          weddingDateLabel={isHtmlExactTemplate
            ? htmlExactDateLabel
            : weddingDate.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          locationCards={locationCards}
          availableGifts={availableGifts}
          galleryPosition={galleryPosition}
          isTemplatePreview={isTemplatePreview}
          onTemplatePreviewRedirect={handleTemplatePreviewRedirect}
          onOpenRsvp={() => setShowRsvpModal(true)}
          onGiftClick={openGiftModal}
          onOpenGallery={openGallery}
          showCountdown={site.show_countdown !== false}
          showGiftSection={site.show_gift_section !== false}
          showRsvpCount={site.show_rsvp_count !== false}
          confirmedGuests={site.confirmed_guests || 0}
        />
      ) : (
        <>
      {/* Hero Section - Template Specific */}
      <section
        className={`relative ${isHtmlExactTemplate ? 'min-h-[76vh] md:min-h-[82vh]' : isDestinationPraiaTemplate ? 'min-h-[68vh] md:min-h-[74vh]' : 'min-h-screen'} flex items-center ${heroStyles.shellClass}`}
        style={{ background: bannerSectionBackgroundColor || undefined }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImageSrc})` }}
        />
        <div
          className="absolute inset-0"
          style={{ background: heroStyles.overlay, opacity: heroOverlayOpacity }}
        />

        {heroStyles.frame && (
          <div className="absolute inset-8 border-4 border-white/30 pointer-events-none z-10" />
        )}
        {heroStyles.arch && (
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[90vw] max-w-4xl h-[55vh] border border-white/40 rounded-t-[999px] border-b-0 pointer-events-none z-10" />
        )}

        {templateProfile.heroLayout === 'split' && (
          <div
            className="absolute inset-0 pointer-events-none opacity-20"
            style={{
              backgroundImage: `linear-gradient(45deg, ${secondaryColor}44 25%, transparent 25%, transparent 50%, ${secondaryColor}44 50%, ${secondaryColor}44 75%, transparent 75%, transparent)`,
              backgroundSize: '80px 80px'
            }}
          />
        )}

        {templateProfile.countdownStyle === 'wreath' && (
          <div className="absolute inset-x-0 top-24 flex justify-center pointer-events-none">
            <div className="w-48 h-48 rounded-full border border-white/40" />
          </div>
        )}

        {['dark-editorial', 'midnight-ballroom'].includes(resolvedTemplateId) && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `radial-gradient(circle at 20% 80%, ${secondaryColor}22 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${secondaryColor}22 0%, transparent 50%)`
            }}
          />
        )}

        <div className={`relative z-10 ${heroStyles.contentClass}`}>
          {templateProfile.heroLayout === 'split' ? (
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div className={`${heroStyles.darkText ? 'text-[#1C1C1C]' : 'text-white'} text-left`}>
                {heroTextContent}
              </div>
              <div className="hidden md:block">
                {isBotanicalGardenTemplate ? (
                  <div className="rounded-[2rem] border border-white/50 bg-white/85 p-6 shadow-2xl backdrop-blur-sm">
                    <p className="text-xs uppercase tracking-[0.24em] text-[#6B7F74]">Botanical Garden</p>
                    <p className="mt-3 text-2xl leading-tight text-[#244034]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      Um dia leve, orgânico e cheio de detalhes naturais.
                    </p>
                    <p className="mt-4 text-sm text-[#4F5E56]">
                      Cerimônia: {site.ceremony_location || 'Local a confirmar'}
                    </p>
                  </div>
                ) : (
                  <div
                    className="aspect-[4/5] rounded-[2rem] border border-white/30 backdrop-blur-sm"
                    style={{ background: `linear-gradient(145deg, ${secondaryColor}55, transparent)` }}
                  />
                )}
              </div>
            </div>
          ) : (
            <div
              className={`relative z-10 ${heroStyles.textAlign === 'left' ? 'text-left' : 'text-center'} ${heroStyles.darkText ? 'text-[#1C1C1C]' : 'text-white'} ${templateProfile.heroLayout === 'panoramic' ? 'max-w-3xl pb-12' : 'max-w-4xl mx-auto'} ${templateProfile.heroLayout === 'editorial' && !isHtmlExactTemplate ? 'md:-ml-10' : ''}`}
            >
              {heroTextContent}
            </div>
          )}
        </div>

        {!isHtmlExactTemplate && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown className={`w-8 h-8 ${heroStyles.darkText ? 'text-[#333333]' : 'text-white/50'}`} />
          </div>
        )}
      </section>

      {isDestinationPraiaTemplate && (
        <section className="px-6 py-8" style={{ background: '#F7FBFE' }}>
          <div className="mx-auto grid max-w-6xl gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-[#D7E7F2] bg-white px-5 py-4">
              <p className="text-[0.65rem] uppercase tracking-[0.24em] text-[#5C8AA7]">Cerimônia</p>
              <p className="mt-2 text-lg font-semibold text-[#1D3F53]">{site.ceremony_time || '--:--'}</p>
              <p className="text-sm text-[#48697E]">{site.ceremony_location || 'Local a confirmar'}</p>
            </div>
            <div className="rounded-2xl border border-[#D7E7F2] bg-white px-5 py-4">
              <p className="text-[0.65rem] uppercase tracking-[0.24em] text-[#5C8AA7]">Recepção</p>
              <p className="mt-2 text-lg font-semibold text-[#1D3F53]">{site.reception_time || '--:--'}</p>
              <p className="text-sm text-[#48697E]">{site.reception_location || 'Local a confirmar'}</p>
            </div>
            <div className="rounded-2xl border border-[#D7E7F2] bg-white px-5 py-4">
              <p className="text-[0.65rem] uppercase tracking-[0.24em] text-[#5C8AA7]">Contagem</p>
              <p className="mt-2 text-lg font-semibold text-[#1D3F53]">{daysUntil} dias</p>
              <p className="text-sm text-[#48697E]">até celebrarmos juntos</p>
            </div>
          </div>
        </section>
      )}

      {isHtmlExactTemplate && (
        <div className="bg-white text-center px-6 pb-8">
          <div
            className="h-16 md:h-24 w-full bg-center bg-no-repeat bg-[length:100%_100%]"
            style={{
              backgroundImage: `url(${HTML_EXACT_DECOR_TOP})`
            }}
          />
          <p className="mt-4 text-[0.72rem] tracking-[0.24em] uppercase text-[#736A60] font-medium">
            Vamos casar!
          </p>
          <p
            className="mt-2 text-xl md:text-2xl text-[#3A3732]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {htmlExactDateLabel}
          </p>
          {htmlExactPlaceLabel ? (
            <p className="mt-2 text-sm md:text-base text-[#5E5A54]">
              {htmlExactPlaceLabel}
            </p>
          ) : null}
          <div
            className="mt-6 h-14 md:h-20 bg-center bg-repeat-x bg-[length:auto_100%]"
            style={{ backgroundImage: `url(${HTML_EXACT_SEPARATOR})` }}
          />
        </div>
      )}

      {galleryPosition === 'after_hero' && gallerySection}

      {/* Story Section */}
      {site.story_text && (
        <section className="py-20 px-6" style={{ background: storySectionBackgroundColor || (isHtmlExactTemplate ? '#FFFFFF' : isDarkTemplate ? '#0F172A' : primaryColor) }}>
          <div
            className={`max-w-5xl mx-auto px-6 md:px-10 py-10 ${storySectionClass}`}
            style={{
              background: isHtmlExactTemplate
                ? 'transparent'
                : templateProfile.storyLayout === 'blocks' || templateProfile.storyLayout === 'timeline'
                  ? 'rgba(255,255,255,0.06)'
                  : templateProfile.storyLayout === 'polaroid'
                    ? 'rgba(255,255,255,0.12)'
                    : 'transparent'
            }}
          >
            <div className={templateProfile.storyLayout === 'timeline' || templateProfile.storyLayout === 'journal' ? 'md:grid md:grid-cols-[180px_1fr] md:items-start md:gap-8' : 'text-center'}>
              <h2
                className={`text-3xl md:text-4xl font-bold mb-8 ${isHtmlExactTemplate ? 'uppercase tracking-[0.08em] text-2xl md:text-3xl font-medium' : ''} ${templateProfile.storyLayout === 'timeline' || templateProfile.storyLayout === 'journal' ? 'md:mb-0 md:pt-2 md:text-left' : ''}`}
                style={{
                  fontFamily: storyTitleFontFamily,
                  color: normalizeHexColor(site.story_title_color) || site.text_color || (isHtmlExactTemplate ? '#252322' : 'white')
                }}
              >
                {isHtmlExactTemplate ? 'Bem-vindos ao nosso casamento!' : 'Nossa História'}
              </h2>
              <p
                className={`text-lg leading-relaxed whitespace-pre-line ${templateProfile.storyLayout === 'timeline' || templateProfile.storyLayout === 'journal' ? `md:border-l ${isHtmlExactTemplate ? 'md:border-[#E8DFD3]' : 'md:border-white/20'} md:pl-6 text-left` : ''}`}
                style={{
                  color: normalizeHexColor(site.story_title_color) || (isHtmlExactTemplate ? bodyTextColor : bodyTextOnPrimary),
                  fontFamily: storyBodyFontFamily
                }}
              >
                {site.story_text}
              </p>
            </div>
          </div>
        </section>
      )}

      {galleryPosition === 'after_story' && gallerySection}

      {/* Location Section with Map */}
      {locationCards.length > 0 && (
        <section className="py-20 px-6" style={{ background: locationSectionBackgroundColor || sectionBackground }}>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
                <MapPin className="w-12 h-12 mx-auto mb-4" style={{ color: locationIconColor || locationTitleColor }} />
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: locationTitleFontFamily, color: locationTitleColor }}>
                Localização
              </h2>
            </div>
            <div className={locationGridClassResolved}>
              {locationCards.map((item) => {
                const Icon = item.icon;
                return (
                    <div
                      key={item.key}
                      className={`rounded-2xl overflow-hidden ${locationCardClass}`}
                      style={locationCardBackgroundColor ? { background: locationCardBackgroundColor } : undefined}
                    >
                      <div className="p-6 text-center">
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                          style={{ background: locationIconColor || item.iconBg }}
                        >
                          <Icon className="w-8 h-8" style={{ color: getAutoContrastTextColor(locationIconColor || item.iconBg, '#FFFFFF') }} />
                      </div>
                      <h3 className="text-xl font-bold mb-2" style={{ fontFamily: locationTitleFontFamily, color: locationTitleColor }}>
                        {item.title}
                      </h3>
                      {item.location ? (
                        <p className="font-medium mb-1" style={{ color: locationTextColor, fontFamily: locationTextFontFamily }}>{item.location}</p>
                      ) : null}
                      {item.address ? (
                        <p className="text-sm" style={{ color: locationTextColor, fontFamily: locationTextFontFamily }}>{item.address}</p>
                      ) : null}
                      {item.time ? (
                        <div className="flex items-center justify-center gap-2 mt-4 text-sm" style={{ color: locationTextColor, fontFamily: locationTextFontFamily }}>
                          <Clock className="w-4 h-4" />
                          {item.time}
                        </div>
                      ) : null}
                      {item.address ? (
                        <a
                          href={getGoogleMapsUrl(item.address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 mt-4 text-sm font-medium hover:underline"
                          style={{ color: locationIconColor || (templateProfile.locationLayout === 'glow' ? '#93C5FD' : primaryColor) }}
                        >
                          <ExternalLink className="w-4 h-4" />
                          Abrir no Google Maps
                        </a>
                      ) : null}
                    </div>
                    {item.address ? (
                      <div className="h-48 bg-slate-100">
                        <iframe
                          src={`https://maps.google.com/maps?q=${encodeURIComponent(item.address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title={item.mapTitle}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {galleryPosition === 'before_gifts' && gallerySection}

      {/* Gift List Section */}
      {site.gifts && site.gifts.length > 0 && (
        <section className="py-20 px-6" style={{ background: giftSectionBackgroundColor }}>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Gift className="w-12 h-12 mx-auto mb-4" style={{ color: giftIconColor }} />
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display', color: giftTitleColor }}>
                Lista de Presentes
              </h2>
              <p style={{ color: giftTextColor }}>Escolha um presente especial para os noivos</p>
            </div>
            <div className="flex justify-center mb-8">
              <div
                className="inline-flex items-center rounded-full px-5 py-2 text-sm font-medium"
                style={{ background: giftCardBackgroundColor, color: giftTextColor }}
              >
                Casamento ({availableGifts.length})
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {availableGifts.map((gift) => (
                <div 
                  key={gift.gift_id} 
                  className={`overflow-hidden ${giftCardClass} hover:shadow-md transition-shadow duration-300`}
                  style={{ background: giftCardBackgroundColor }}
                >
                  <img
                    src={getSafeImageSrc(gift.image_url)}
                    onError={withImageFallback}
                    alt={gift.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <h3 className="font-bold text-lg mb-2" style={{ color: giftTitleColor }}>{gift.name}</h3>
                    {gift.description && (
                      <p className="text-sm mb-4 line-clamp-2" style={{ color: giftTextColor }}>{gift.description}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold" style={{ color: giftPriceColor }}>
                        R$ {gift.price.toFixed(2)}
                      </span>
                      <Button
                        onClick={() => openGiftModal(gift)}
                        className={`px-6 ${templateProfile.giftStyle === "sharp" ? "rounded-none" : "rounded-full"}`}
                        style={{ background: giftButtonBackgroundColor, color: giftButtonTextColor }}
                        data-testid={`gift-btn-${gift.gift_id}`}
                      >
                        Presentear
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {availableGifts.length === 0 && (
              <div className="text-center py-12">
                <Gift className="w-16 h-16 mx-auto mb-4" style={{ color: giftIconColor }} />
                <p style={{ color: giftTextColor }}>Nenhum presente disponível no momento</p>
              </div>
            )}
          </div>
        </section>
      )}

      {galleryPosition === 'end' && gallerySection}

      {/* RSVP Section */}
      <section className={`py-20 px-6 text-center text-white ${rsvpSectionClass}`} style={{ background: rsvpSectionBackgroundColor || (templateProfile.rsvpStyle === "glow" ? "#0F172A" : primaryColor) }}>
        {templateProfile.rsvpStyle === "glow" && (
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: `radial-gradient(circle at 20% 80%, ${secondaryColor}33 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${secondaryColor}22 0%, transparent 50%)` }} />
        )}
        <Heart className="w-12 h-12 mx-auto mb-6 relative z-10" style={{ color: rsvpIconColor || rsvpTextColor || 'white' }} />
        <h2 className="relative z-10 text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: rsvpTitleFontFamily, color: rsvpTextColor || 'white' }}>
          Confirme sua presença
        </h2>
        <p className="relative z-10 mb-8 max-w-md mx-auto" style={{ color: rsvpTextColor || 'rgba(255,255,255,0.8)', fontFamily: rsvpTextFontFamily }}>
          Ficamos muito felizes com a sua presença no nosso grande dia!
        </p>
        <Button
          onClick={() => (isTemplatePreview ? handleTemplatePreviewRedirect() : setShowRsvpModal(true))}
          className={`${rsvpButtonClass} relative z-10 font-bold text-lg`}
          style={templateProfile.rsvpStyle === "outline" || templateProfile.rsvpStyle === "ticket" ? { background: "transparent", color: confirmButtonTextColor, borderColor: confirmButtonBorderColor } : { background: confirmButtonBackgroundColor, color: confirmButtonTextColor, borderColor: confirmButtonBorderColor }}
        >
          Confirmar presença
        </Button>
        {site.confirmed_guests > 0 && (
          <p className="relative z-10 mt-6" style={{ color: rsvpTextColor || 'rgba(255,255,255,0.6)', fontFamily: rsvpTextFontFamily }}>
            {site.confirmed_guests} pessoa{site.confirmed_guests > 1 ? 's' : ''} já confirmaram
          </p>
        )}
      </section>

      {/* Footer */}
      <footer className="py-8 text-center bg-white">
        <img src={brandMark} alt="Virei Noiva" className="w-6 h-6 mx-auto mb-2 object-contain" />
        <p className="text-sm" style={{ color: bodyTextColor }}>{site.couple_names}</p>
      </footer>
      </>
      )}

      {/* Gallery Modal */}
      {showGalleryModal && site.gallery_images && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90" onClick={() => setShowGalleryModal(false)}>
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <img 
            src={site.gallery_images[galleryIndex]} 
            alt={`Gallery ${galleryIndex + 1}`}
            className="max-w-full max-h-[80vh] object-contain"
            onClick={e => e.stopPropagation()}
          />
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={() => setShowGalleryModal(false)}
            className="absolute top-4 right-4 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70">
            {galleryIndex + 1} / {site.gallery_images.length}
          </div>
        </div>
      )}

      {/* RSVP Modal */}
      <Dialog open={showRsvpModal} onOpenChange={resetRsvpModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Playfair Display', color: primaryColor }}>
              {rsvpStep === 'success' ? 'presença Confirmada!' : 'Confirmar presença'}
            </DialogTitle>
          </DialogHeader>
          
          {/* Success State */}
          {rsvpStep === 'success' && (
            <div className="text-center py-6">
              <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: `${secondaryColor}20` }}>
                <Check className="w-10 h-10" style={{ color: secondaryColor }} />
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: primaryColor }}>
                {selectedGuest?.name || rsvpData.guest_name}
              </h3>
              <p className="text-[#333333] mb-6">
                Sua presença foi confirmada! Nos vemos em breve.
              </p>
              <Button 
                onClick={resetRsvpModal}
                className="rounded-full text-white"
                style={{ background: primaryColor }}
              >
                Fechar
              </Button>
            </div>
          )}
          
          {/* Predefined Mode - Step 1: Search Name */}
          {rsvpMode === 'predefined' && rsvpStep === 'name' && (
            <form onSubmit={handleRsvpSubmit} className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-amber-700">
                  Digite seu nome como está no convite para Confirmar presença.
                </p>
              </div>
              <div>
                <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Seu Nome Completo
                </Label>
                <Input
                  value={rsvpData.guest_name}
                  onChange={(e) => setRsvpData(prev => ({ ...prev, guest_name: e.target.value }))}
                  placeholder="Ex: Maria Silva"
                  className="h-12 rounded-lg"
                  required
                  data-testid="rsvp-name-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={resetRsvpModal} className="rounded-full">
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={searchingGuest || !rsvpData.guest_name.trim()}
                  className="rounded-full text-white"
                  style={{ background: primaryColor }}
                >
                  {searchingGuest ? 'Buscando...' : 'Buscar Meu Nome'}
                </Button>
              </DialogFooter>
            </form>
          )}
          
          {/* Predefined Mode - Step 2: Search Results */}
          {rsvpMode === 'predefined' && rsvpStep === 'search_result' && (
            <div className="space-y-4">
              <p className="text-[#333333]">Encontramos {searchResults.length} resultado(s) para "{rsvpData.guest_name}":</p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {searchResults.map((guest) => (
                  <button
                    key={guest.guest_id}
                    onClick={() => handleSelectGuest(guest)}
                    disabled={guest.confirmed}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      guest.confirmed 
                        ? 'border-green-200 bg-green-50 cursor-not-allowed' 
                        : 'border-slate-200 hover:border-[#E8B7B1] hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium" style={{ color: primaryColor }}>{guest.name}</span>
                      {guest.confirmed && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                          Já confirmou
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              <div className="pt-2 border-t">
                <button 
                  onClick={() => setRsvpStep('name')} 
                  className="text-sm hover:underline"
                  style={{ color: primaryColor }}
                >
                  Buscar outro nome
                </button>
              </div>
            </div>
          )}
          
          {/* Predefined Mode - Not Found */}
          {rsvpMode === 'predefined' && rsvpStep === 'not_found' && (
            <div className="text-center py-6">
              <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                <User className="w-10 h-10 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-[#1C1C1C] mb-2">
                Nome não encontrado
              </h3>
              <p className="text-[#333333] mb-6">
                Não encontramos "{rsvpData.guest_name}" na lista de convidados. 
                Por favor, entre em contato com os noivos.
              </p>
              <div className="flex gap-3 justify-center">
                <Button 
                  variant="outline"
                  onClick={() => setRsvpStep('name')}
                  className="rounded-full"
                >
                  Tentar novamente
                </Button>
                <Button 
                  onClick={resetRsvpModal}
                  className="rounded-full text-white"
                  style={{ background: primaryColor }}
                >
                  Fechar
                </Button>
              </div>
            </div>
          )}
          
          {/* Predefined Mode - Step 3: Confirm */}
          {rsvpMode === 'predefined' && rsvpStep === 'confirm' && selectedGuest && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-sm text-[#333333] mb-1">Confirmando presença de</p>
                <p className="text-lg font-bold" style={{ color: primaryColor }}>{selectedGuest.name}</p>
              </div>
              <div>
                <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Mensagem para os noivos (opcional)
                </Label>
                <Textarea
                  value={rsvpData.message}
                  onChange={(e) => setRsvpData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Deixe uma mensagem carinhosa..."
                  className="rounded-lg"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setRsvpStep('search_result')} className="rounded-full">
                  Voltar
                </Button>
                <Button 
                  onClick={handleConfirmPredefined}
                  disabled={submitting}
                  className="rounded-full text-white"
                  style={{ background: secondaryColor }}
                  data-testid="submit-rsvp-btn"
                >
                  {submitting ? 'Confirmando...' : 'Confirmar presença'}
                </Button>
              </DialogFooter>
            </div>
          )}
          
          {/* Open Mode - Simple Form */}
          {rsvpMode === 'open' && rsvpStep !== 'success' && (
            <form onSubmit={handleRsvpSubmit} className="space-y-4">
              <div>
                <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Nome e Sobrenome *
                </Label>
                <Input
                  value={rsvpData.guest_name}
                  onChange={(e) => setRsvpData(prev => ({ ...prev, guest_name: e.target.value }))}
                  placeholder="Ex: Maria Silva"
                  className="h-12 rounded-lg"
                  required
                  data-testid="rsvp-name-input"
                />
              </div>
              <div>
                <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  E-mail (opcional)
                </Label>
                <Input
                  type="email"
                  value={rsvpData.guest_email}
                  onChange={(e) => setRsvpData(prev => ({ ...prev, guest_email: e.target.value }))}
                  placeholder="seu@email.com"
                  className="h-12 rounded-lg"
                />
              </div>
              <div>
                <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Você irá comparecer?
                </Label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setRsvpData(prev => ({ ...prev, attending: true }))}
                    className={`flex-1 h-12 rounded-lg border-2 font-medium transition-all duration-200 ${
                      rsvpData.attending 
                        ? 'border-green-500 bg-green-50 text-green-700' 
                        : 'border-slate-200 text-[#333333]'
                    }`}
                  >
                    <Check className="w-5 h-5 inline mr-2" />
                    Sim
                  </button>
                  <button
                    type="button"
                    onClick={() => setRsvpData(prev => ({ ...prev, attending: false }))}
                    className={`flex-1 h-12 rounded-lg border-2 font-medium transition-all duration-200 ${
                      !rsvpData.attending 
                        ? 'border-red-500 bg-red-50 text-red-700' 
                        : 'border-slate-200 text-[#333333]'
                    }`}
                  >
                    Não poderei ir
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Mensagem (opcional)
                </Label>
                <Textarea
                  value={rsvpData.message}
                  onChange={(e) => setRsvpData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Deixe uma mensagem para os noivos..."
                  className="rounded-lg"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={resetRsvpModal} className="rounded-full">
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting || !rsvpData.guest_name.trim()}
                  className="rounded-full text-white"
                  style={{ background: primaryColor }}
                  data-testid="submit-rsvp-btn"
                >
                  {submitting ? 'Enviando...' : 'Confirmar'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Gift Payment Modal */}
      <Dialog open={showGiftModal} onOpenChange={closeGiftModal}>
        <DialogContent className={`max-h-[90vh] overflow-y-auto ${paymentStep === 'card_form' ? 'max-w-lg' : 'max-w-md'}`}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Playfair Display', color: primaryColor }}>
              {paymentStep === 'card_form' ? 'Pagamento com cartão' : paymentStep === 'processing' ? (pixPayment ? 'Pagamento PIX' : 'Processando') : 'Presentear'}
            </DialogTitle>
          </DialogHeader>
          
          {/* Step 1: Info - Name and Email */}
          {selectedGift && paymentStep === 'info' && !giftCheckoutStatus.available && (
            <div className="space-y-4" role="status" data-testid="gift-checkout-maintenance">
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-900">
                <h3 className="font-bold">{giftCheckoutStatus.loading ? 'Verificando disponibilidade' : 'Presentes em manutenção'}</h3>
                <p className="mt-2 text-sm">
                  {giftCheckoutStatus.loading
                    ? 'Aguarde um instante antes de continuar.'
                    : 'Estamos ajustando os pagamentos. Novas compras de presentes estarão disponíveis em breve.'}
                </p>
              </div>
              <Button type="button" variant="outline" onClick={closeGiftModal} className="rounded-full">Fechar</Button>
            </div>
          )}

          {selectedGift && paymentStep === 'info' && giftCheckoutStatus.available && (
            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              {giftCheckoutStatus.maintenance && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">
                  Acesso de teste do administrador. Esta compra será real.
                </p>
              )}
              <div className="bg-slate-50 rounded-lg p-4 flex items-center gap-4">
                <img
                  src={getSafeImageSrc(selectedGift.image_url)}
                  onError={withImageFallback}
                  alt={selectedGift.name}
                  className="w-16 h-16 rounded-lg object-cover"
                />
                <div>
                  <h4 className="font-bold" style={{ color: primaryColor }}>{selectedGift.name}</h4>
                  <p className="font-bold" style={{ color: secondaryColor }}>R$ {selectedGift.price.toFixed(2)}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Seu Nome
                </Label>
                <Input
                  value={paymentData.payer_name}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, payer_name: e.target.value }))}
                  placeholder="Nome completo"
                  className="h-12 rounded-lg"
                  required
                  data-testid="payment-name-input"
                />
              </div>
              <div>
                <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Seu E-mail
                </Label>
                <Input
                  type="email"
                  value={paymentData.payer_email}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, payer_email: e.target.value }))}
                  placeholder="seu@email.com"
                  className="h-12 rounded-lg"
                  required
                  data-testid="payment-email-input"
                />
              </div>
              {GIFT_PAYMENT_PROVIDER === 'pagarme' && (
                <div>
                  <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                    Seu Celular
                  </Label>
                  <Input
                    type="tel"
                    value={paymentData.payer_phone}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, payer_phone: e.target.value }))}
                    placeholder="(21) 99999-9999"
                    className="h-12 rounded-lg"
                    required
                    data-testid="payment-phone-input"
                  />
                </div>
              )}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                    Documento
                  </Label>
                  <select
                    value={paymentData.doc_type}
                    onChange={(e) => setPaymentData(prev => ({
                      ...prev,
                      doc_type: e.target.value,
                      doc_number: formatDocument(prev.doc_number || '', e.target.value)
                    }))}
                    className="w-full h-12 rounded-lg border border-slate-200 px-3 bg-white"
                  >
                    <option value="CPF">CPF</option>
                    <option value="CNPJ">CNPJ</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                    Número
                  </Label>
                  <Input
                    type="text"
                    value={paymentData.doc_number}
                    onChange={(e) => setPaymentData(prev => ({
                      ...prev,
                      doc_number: formatDocument(e.target.value, prev.doc_type)
                    }))}
                    placeholder={paymentData.doc_type === 'CPF' ? '000.000.000-00' : '00.000.000/0000-00'}
                    className="h-12 rounded-lg"
                    required
                    data-testid="payment-document-input"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeGiftModal} className="rounded-full">
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="rounded-full text-white"
                  style={{ background: primaryColor }}
                >
                  Continuar
                </Button>
              </DialogFooter>
            </form>
          )}
          
          {/* Step 2: Payment Method Selection */}
          {selectedGift && paymentStep === 'method' && (
            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
                <div>
                  <p className="text-sm text-[#333333]">Presente para</p>
                  <p className="font-bold" style={{ color: primaryColor }}>{selectedGift.name}</p>
                </div>
              </div>
              
              <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider block">
                Escolha como pagar
              </Label>
              
              {/* PIX Option */}
              <button
                type="button"
                onClick={() => handlePaymentMethodSelect('pix')}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  paymentData.payment_method === 'pix' 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    paymentData.payment_method === 'pix' ? 'bg-green-500' : 'bg-slate-100'
                  }`}>
                    <QrCode className={`w-6 h-6 ${paymentData.payment_method === 'pix' ? 'text-white' : 'text-[#333333]'}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`font-bold ${paymentData.payment_method === 'pix' ? 'text-green-700' : 'text-[#1C1C1C]'}`}>
                      PIX
                    </p>
                    <p className="text-sm text-[#333333]">Pagamento instantâneo</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold" style={{ color: secondaryColor }}>R$ {selectedGift.price.toFixed(2)}</p>
                  </div>
                </div>
              </button>
              
              {/* Credit Card Option */}
              <button
                type="button"
                onClick={() => handlePaymentMethodSelect('card')}
                disabled={!cardPaymentEnabled}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  !cardPaymentEnabled
                    ? 'border-slate-200 bg-slate-100 cursor-not-allowed opacity-60'
                    : paymentData.payment_method === 'card'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    paymentData.payment_method === 'card' ? 'bg-blue-500' : 'bg-slate-100'
                  }`}>
                    <CreditCard className={`w-6 h-6 ${paymentData.payment_method === 'card' ? 'text-white' : 'text-[#333333]'}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`font-bold ${paymentData.payment_method === 'card' ? 'text-blue-700' : 'text-[#1C1C1C]'}`}>
                      Cartão de crédito
                    </p>
                    <p className="text-sm text-[#333333]">{cardHintText}</p>
                  </div>
                </div>
              </button>
              
              {/* Installments for Card */}
              {paymentData.payment_method === 'card' && installments.length > 0 && (
                <div className="mt-4 p-4 bg-slate-50 rounded-xl">
                  <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-3 block">
                    Parcelas
                  </Label>
                  <select
                    value={selectedInstallment}
                    onChange={(e) => setSelectedInstallment(parseInt(e.target.value))}
                    className="w-full h-12 rounded-lg border border-slate-200 px-4 bg-white"
                  >
                    {installments.map((inst) => (
                      <option key={inst.installments} value={inst.installments}>
                        {`${inst.installments}x de ${formatBRL(inst.installment_amount)}`}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-[#333333] mt-2">
                    {coupleAbsorbsCardFee
                      ? `O casal assumiu a taxa do provedor. Valor final para o convidado: ${formatBRL(selectedInstallmentOption?.total_amount || selectedGift.price)}`
                      : selectedInstallmentOption?.has_interest
                      ? `Taxa do provedor aplicada (${Number(selectedInstallmentOption?.provider_surcharge_percent ?? selectedInstallmentOption?.interest_rate ?? 0).toFixed(2)}%). Valor final: ${formatBRL(selectedInstallmentOption.total_amount)}`
                      : `Sem taxa adicional configurada para esta parcela. Valor final: ${formatBRL(selectedInstallmentOption?.total_amount || selectedGift.price)}`}
                  </p>
                </div>
              )}

              {paymentData.payment_method === 'pix' && GIFT_PAYMENT_PROVIDER === 'pagarme' && (
                <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-bold text-[#1C1C1C]">Endereço do comprador</p>
                  <p className="text-xs text-[#555555]">Necessário para processar o PIX na Stone. Não é endereço de entrega.</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <Label className="text-sm font-bold text-[#1C1C1C] mb-1 block">CEP</Label>
                      <Input
                        value={cardFormData.postalCode}
                        onChange={(e) => handlePostalCodeChange(e.target.value)}
                        placeholder="00000-000"
                        maxLength={9}
                        required
                        data-testid="pix-postal-code-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-bold text-[#1C1C1C] mb-1 block">Número</Label>
                      <Input
                        value={cardFormData.addressNumber}
                        onChange={(e) => setCardFormData(prev => ({ ...prev, addressNumber: e.target.value.replace(/[^\dA-Za-z/-]/g, '').substring(0, 10) }))}
                        placeholder="123"
                        required
                        data-testid="pix-address-number-input"
                      />
                    </div>
                  </div>
                  {postalCodeLookup.status === 'loading' && (
                    <p className="text-sm text-slate-600" role="status">Buscando endereço...</p>
                  )}
                  {postalCodeLookup.status === 'success' && hasCompleteCardAddress && !editingCardAddress && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm" data-testid="pix-address-summary">
                      <p>{cardFormData.addressStreet}, {cardFormData.addressNeighborhood} - {cardFormData.addressCity}/{cardFormData.addressState}</p>
                      <button type="button" onClick={() => setEditingCardAddress(true)} className="mt-1 font-semibold text-emerald-800 underline">Editar endereço</button>
                    </div>
                  )}
                  {['error', 'partial'].includes(postalCodeLookup.status) && (
                    <p className="text-xs text-amber-700" role="status">{postalCodeLookup.message}</p>
                  )}
                  {showManualCardAddress && (
                    <>
                      <div>
                        <Label className="text-sm font-bold text-[#1C1C1C] mb-1 block">Rua ou avenida</Label>
                        <Input
                          value={cardFormData.addressStreet}
                          onChange={(e) => setCardFormData(prev => ({ ...prev, addressStreet: e.target.value.substring(0, 120) }))}
                          required
                          data-testid="pix-address-street-input"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-bold text-[#1C1C1C] mb-1 block">Bairro</Label>
                        <Input
                          value={cardFormData.addressNeighborhood}
                          onChange={(e) => setCardFormData(prev => ({ ...prev, addressNeighborhood: e.target.value.substring(0, 80) }))}
                          required
                          data-testid="pix-address-neighborhood-input"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                          <Label className="text-sm font-bold text-[#1C1C1C] mb-1 block">Cidade</Label>
                          <Input
                            value={cardFormData.addressCity}
                            onChange={(e) => setCardFormData(prev => ({ ...prev, addressCity: e.target.value.substring(0, 64) }))}
                            required
                            data-testid="pix-address-city-input"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-bold text-[#1C1C1C] mb-1 block">UF</Label>
                          <Input
                            value={cardFormData.addressState}
                            onChange={(e) => setCardFormData(prev => ({ ...prev, addressState: e.target.value.replace(/[^A-Za-z]/g, '').substring(0, 2).toUpperCase() }))}
                            maxLength={2}
                            required
                            data-testid="pix-address-state-input"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div>
                    <Label className="text-sm font-bold text-[#1C1C1C] mb-1 block">Complemento (opcional)</Label>
                    <Input
                      value={cardFormData.addressComplement}
                      onChange={(e) => setCardFormData(prev => ({ ...prev, addressComplement: e.target.value.substring(0, 60) }))}
                      placeholder="Apto, bloco, casa"
                      data-testid="pix-address-complement-input"
                    />
                  </div>
                </div>
              )}

              <p className="text-xs text-[#333333]">
                {coupleAbsorbsCardFee
                  ? 'Nesta compra, o casal decidiu absorver a taxa do cartão para o convidado pagar apenas o valor do presente.'
                  : 'Taxas do provedor podem aparecer no extrato do banco, mesmo quando a taxa da plataforma for diferente.'}
              </p>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setPaymentStep('info')} 
                  className="rounded-full"
                >
                  Voltar
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className={`rounded-full text-white ${
                    paymentData.payment_method === 'pix' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                  data-testid="submit-payment-btn"
                >
                  {submitting ? 'Processando...' : (
                    paymentData.payment_method === 'pix' 
                      ? `Pagar ${formatBRL(selectedGift.price)} via PIX`
                      : `Pagar ${selectedInstallment}x de ${formatBRL(selectedInstallmentOption?.installment_amount || selectedGift.price)}`
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
          
          {/* Step 3: PIX QR Code Display */}
          {paymentStep === 'processing' && pixPayment && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 flex items-center gap-4">
                <img
                    src={getSafeImageSrc(selectedGift?.image_url)}
                    onError={withImageFallback}
                    alt={selectedGift?.name || 'Presente'}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                <div>
                  <h4 className="font-medium text-sm" style={{ color: primaryColor }}>{selectedGift?.name}</h4>
                  <p className="font-bold" style={{ color: secondaryColor }}>R$ {pixPayment.amount?.toFixed(2)}</p>
                </div>
              </div>

              {/* QR Code */}
              <div className="text-center">
                <div className="bg-white p-4 rounded-xl inline-block border-2 border-slate-200">
                  {pixPayment.qr_code || pixPayment.qr_code_image_url ? (
                    <img 
                      src={pixPayment.qr_code_image_url || `data:image/png;base64,${pixPayment.qr_code}`}
                      alt="QR Code PIX"
                      className="w-48 h-48"
                    />
                  ) : (
                    <div className="w-48 h-48 flex items-center justify-center">
                      <QrCode className="w-24 h-24 text-[#333333]" />
                    </div>
                  )}
                </div>
                <p className="text-sm text-[#333333] mt-2">
                  Escaneie o QR Code com o app do seu banco
                </p>
                {(pixPayment.is_test_mode || pixPayment.payment_environment === 'test') && (
                  <div className="mt-3 text-left bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-amber-800">Modo teste do provedor de pagamento</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Este QR Code e código PIX não funcionam para cobrança real. Configure credenciais de produção para receber pagamentos.
                    </p>
                  </div>
                )}
              </div>

              {/* PIX Copy-Paste */}
              <div>
                <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Ou copie o código PIX
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={pixPayment.qr_code_text || ''}
                    readOnly
                    className="h-12 rounded-lg text-xs font-mono bg-slate-50"
                  />
                  <Button
                    type="button"
                    onClick={copyPixCode}
                    className="h-12 px-4 rounded-lg bg-white hover:bg-[#F6F1EF]"
                    data-testid="copy-pix-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Payment Status */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      Verificando pagamento automaticamente...
                    </p>
                    <p className="text-xs text-green-600">
                      A tela atualizará assim que o pagamento for confirmado
                    </p>
                  </div>
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={closeGiftModal} 
                  className="rounded-full w-full sm:w-auto"
                >
                  Cancelar
                </Button>
                <Button 
                  type="button"
                  onClick={checkPaymentStatus}
                  disabled={checkingPayment}
                  className="rounded-full w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
                  data-testid="check-payment-btn"
                >
                  {checkingPayment ? 'Verificando...' : 'Verificar Agora'}
                </Button>
              </DialogFooter>
            </div>
          )}
          
          {/* Step 4: Card Payment Form */}
          {paymentStep === 'card_form' && selectedGift && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
                <CreditCard className="w-8 h-8 text-blue-500" />
                <div className="flex-1">
                  <p className="text-sm text-[#333333]">Pagando</p>
                  <p className="font-bold" style={{ color: primaryColor }}>{selectedGift.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm" style={{ color: secondaryColor }}>
                    {selectedInstallment}x de {formatBRL(selectedInstallmentOption?.installment_amount || selectedGift.price)}
                  </p>
                  {selectedInstallmentOption?.has_interest && (
                    <p className="text-xs text-[#333333]">Com juros</p>
                  )}
                </div>
              </div>
              
              {processingCard ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-3" />
                  <span className="text-[#333333] font-medium">Processando pagamento...</span>
                  <span className="text-[#333333] text-sm">Não feche esta janela</span>
                </div>
              ) : (
                <form onSubmit={processCardForm} className="space-y-4">
                  {/* Card Number */}
                  <div>
                    <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                      Número do cartão
                    </Label>
                    <Input
                      name="card_number"
                      type="text"
                      value={cardFormData.cardNumber}
                      onChange={(e) => setCardFormData(prev => ({ ...prev, cardNumber: formatCardNumber(e.target.value) }))}
                      placeholder="0000 0000 0000 0000"
                      maxLength={19}
                      className="h-12 rounded-lg font-mono text-lg"
                      required
                      data-testid="card-number-input"
                    />
                  </div>
                  
                  {/* Expiry and CVV */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                        Validade
                      </Label>
                      <Input
                        name="card_expiry"
                        type="text"
                        value={cardFormData.cardExpiry}
                        onChange={(e) => setCardFormData(prev => ({ ...prev, cardExpiry: formatExpiry(e.target.value) }))}
                        placeholder="MM/AA"
                        maxLength={5}
                        className="h-12 rounded-lg font-mono"
                        required
                        data-testid="card-expiry-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                        CVV
                      </Label>
                      <Input
                        name="card_cvv"
                        type="text"
                        value={cardFormData.cardCvv}
                        onChange={(e) => setCardFormData(prev => ({ ...prev, cardCvv: e.target.value.replace(/\D/g, '').substring(0, 4) }))}
                        placeholder="123"
                        maxLength={4}
                        className="h-12 rounded-lg font-mono"
                        required
                        data-testid="card-cvv-input"
                      />
                    </div>
                  </div>
                  
                  {/* Cardholder Name */}
                  <div>
                    <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                      Nome no cartão
                    </Label>
                    <Input
                      name="cardholder_name"
                      type="text"
                      value={cardFormData.cardholderName}
                      onChange={(e) => setCardFormData(prev => ({ ...prev, cardholderName: e.target.value.toUpperCase() }))}
                      placeholder="NOME COMO ESTÁ NO CARTÃO"
                      className="h-12 rounded-lg uppercase"
                      required
                      data-testid="card-name-input"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                      Celular do titular
                    </Label>
                    <Input
                      name="cardholder_phone"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      value={cardFormData.cardholderPhone}
                      onChange={(e) => setCardFormData(prev => ({ ...prev, cardholderPhone: formatCardholderPhone(e.target.value) }))}
                      placeholder="(11) 99999-9999"
                      maxLength={15}
                      className="h-12 rounded-lg"
                      required
                      data-testid="card-phone-input"
                    />
                  </div>
                  
                  {/* Document */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                        Documento
                      </Label>
                      <select
                        name="doc_type"
                        value={cardFormData.docType}
                        onChange={(e) => setCardFormData(prev => ({
                          ...prev,
                          docType: e.target.value,
                          docNumber: formatDocument(prev.docNumber || '', e.target.value)
                        }))}
                        className="w-full h-12 rounded-lg border border-slate-200 px-3 bg-white"
                      >
                        <option value="CPF">CPF</option>
                        <option value="CNPJ">CNPJ</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                        Número
                      </Label>
                      <Input
                        name="doc_number"
                        type="text"
                        value={cardFormData.docNumber}
                        onChange={(e) => setCardFormData(prev => ({ ...prev, docNumber: formatDocument(e.target.value, prev.docType) }))}
                        placeholder={cardFormData.docType === 'CPF' ? '000.000.000-00' : '00.000.000/0000-00'}
                        className="h-12 rounded-lg"
                        required
                        data-testid="card-doc-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                        CEP
                      </Label>
                      <Input
                        name="postal_code"
                        type="text"
                        value={cardFormData.postalCode}
                        onChange={(e) => handlePostalCodeChange(e.target.value)}
                        placeholder="00000-000"
                        maxLength={9}
                        className="h-12 rounded-lg"
                        required
                        data-testid="card-postal-code-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                        Número
                      </Label>
                      <Input
                        name="address_number"
                        type="text"
                        value={cardFormData.addressNumber}
                        onChange={(e) => setCardFormData(prev => ({ ...prev, addressNumber: e.target.value.replace(/[^\dA-Za-z/-]/g, '').substring(0, 10) }))}
                        placeholder="123"
                        className="h-12 rounded-lg"
                        required
                        data-testid="card-address-number-input"
                      />
                    </div>
                  </div>

                  {GIFT_PAYMENT_PROVIDER === 'pagarme' && cardPostalCodeDigits.length < 8 && (
                    <p className="text-xs text-slate-500 -mt-2">
                      Digite o CEP para preenchermos o endereço automaticamente.
                    </p>
                  )}

                  {GIFT_PAYMENT_PROVIDER === 'pagarme' && postalCodeLookup.status === 'loading' && (
                    <div className="flex items-center gap-2 text-sm text-slate-600" role="status">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Buscando endereço...
                    </div>
                  )}

                  {GIFT_PAYMENT_PROVIDER === 'pagarme' && postalCodeLookup.status === 'success' && hasCompleteCardAddress && !editingCardAddress && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="card-address-summary">
                      <div className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-emerald-900">Endereço encontrado</p>
                          <p className="text-sm text-emerald-800 mt-1">
                            {cardFormData.addressStreet}, {cardFormData.addressNeighborhood} - {cardFormData.addressCity}/{cardFormData.addressState}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setEditingCardAddress(true)}
                          className="text-xs font-semibold text-emerald-800 underline underline-offset-2 shrink-0"
                        >
                          Editar
                        </button>
                      </div>
                    </div>
                  )}

                  {GIFT_PAYMENT_PROVIDER === 'pagarme' && ['error', 'partial'].includes(postalCodeLookup.status) && (
                    <p className="text-xs text-amber-700 -mt-2" role="status">
                      {postalCodeLookup.message}
                    </p>
                  )}

                  {showManualCardAddress && (
                    <>
                      <div>
                        <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                          Rua ou avenida
                        </Label>
                        <Input
                          name="address_street"
                          type="text"
                          value={cardFormData.addressStreet}
                          onChange={(e) => setCardFormData(prev => ({ ...prev, addressStreet: e.target.value.substring(0, 120) }))}
                          placeholder="Ex: Rua das Flores"
                          className="h-12 rounded-lg"
                          required
                          data-testid="card-address-street-input"
                        />
                      </div>

                      <div>
                        <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                          Bairro
                        </Label>
                        <Input
                          name="address_neighborhood"
                          type="text"
                          value={cardFormData.addressNeighborhood}
                          onChange={(e) => setCardFormData(prev => ({ ...prev, addressNeighborhood: e.target.value.substring(0, 80) }))}
                          placeholder="Ex: Centro"
                          className="h-12 rounded-lg"
                          required
                          data-testid="card-address-neighborhood-input"
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div className="col-span-2">
                          <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                            Cidade
                          </Label>
                          <Input
                            name="address_city"
                            type="text"
                            value={cardFormData.addressCity}
                            onChange={(e) => setCardFormData(prev => ({ ...prev, addressCity: e.target.value.substring(0, 64) }))}
                            placeholder="Ex: Rio de Janeiro"
                            className="h-12 rounded-lg"
                            required
                            data-testid="card-address-city-input"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                            UF
                          </Label>
                          <Input
                            name="address_state"
                            type="text"
                            value={cardFormData.addressState}
                            onChange={(e) => setCardFormData(prev => ({ ...prev, addressState: e.target.value.replace(/[^A-Za-z]/g, '').substring(0, 2).toUpperCase() }))}
                            placeholder="RJ"
                            maxLength={2}
                            className="h-12 rounded-lg uppercase"
                            required
                            data-testid="card-address-state-input"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                      Complemento
                    </Label>
                    <Input
                      name="address_complement"
                      type="text"
                      value={cardFormData.addressComplement}
                      onChange={(e) => setCardFormData(prev => ({ ...prev, addressComplement: e.target.value.substring(0, 60) }))}
                      placeholder="Apto, bloco, casa (opcional)"
                      className="h-12 rounded-lg"
                      data-testid="card-address-complement-input"
                    />
                  </div>
                  
                  {/* Error message */}
                  {cardError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-600">{cardError}</p>
                    </div>
                  )}
                  
                  {/* Accepted cards */}
                  <div className="flex items-center justify-center gap-4 py-2 text-[#333333]">
                    <span className="text-xs font-medium">Aceitamos:</span>
                    <span className="text-sm font-bold">VISA</span>
                    <span className="text-sm font-bold">Mastercard</span>
                    <span className="text-sm font-bold">Elo</span>
                    <span className="text-sm font-bold">Amex</span>
                  </div>
                  
                  {/* Actions */}
                  <div className="pt-2 flex gap-2">
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setPaymentStep('method');
                        setCardError('');
                        setCardFormData({
                          cardNumber: '',
                          cardExpiry: '',
                          cardCvv: '',
                          cardholderName: '',
                          cardholderPhone: '',
                          docType: 'CPF',
                          docNumber: '',
                          postalCode: '',
                          addressNumber: '',
                          addressComplement: '',
                          addressStreet: '',
                          addressNeighborhood: '',
                          addressCity: '',
                          addressState: ''
                        });
                        setPostalCodeLookup({ status: 'idle', message: '' });
                        setEditingCardAddress(false);
                      }} 
                      className="rounded-full flex-1"
                    >
                      Voltar
                    </Button>
                    <Button 
                      type="submit"
                      className="rounded-full flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="submit-card-btn"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Pagar {formatBRL(selectedInstallmentOption?.total_amount || selectedGift.price)}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}
          
          {/* Card Payment Success */}
          {paymentStep === 'success' && cardPayment && (
            <div className="space-y-4 text-center py-6">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold" style={{ color: primaryColor }}>
                Pagamento Aprovado!
              </h3>
              <p className="text-[#333333]">
                Obrigado pelo presente! Os noivos vão adorar.
              </p>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-sm text-green-700">
                  <strong>{selectedGift?.name}</strong> foi marcado como presenteado.
                </p>
              </div>
              <DialogFooter>
                <Button 
                  type="button" 
                  onClick={closeGiftModal}
                  className="rounded-full text-white"
                  style={{ background: primaryColor }}
                >
                  Fechar
                </Button>
              </DialogFooter>
            </div>
          )}
          
          {/* Card Payment Pending */}
          {paymentStep === 'pending' && cardPayment && (
            <div className="space-y-4 text-center py-6">
              <div className="w-20 h-20 mx-auto bg-amber-100 rounded-full flex items-center justify-center">
                <Clock className="w-10 h-10 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold" style={{ color: primaryColor }}>
                Pagamento em análise
              </h3>
              <p className="text-[#333333]">
                Seu pagamento está sendo processado. Você receberá uma confirmação em breve.
              </p>
              {cardPayment?.checkout_url && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-left">
                  <p className="text-sm text-blue-700 mb-3">
                    Se o checkout seguro não abriu automaticamente, clique abaixo para continuar.
                  </p>
                  <Button
                    type="button"
                    className="rounded-full text-white"
                    style={{ background: '#2563EB' }}
                    onClick={() => window.open(cardPayment.checkout_url, '_blank', 'noopener,noreferrer')}
                  >
                    Abrir checkout seguro
                  </Button>
                </div>
              )}
              <DialogFooter>
                <Button 
                  type="button" 
                  onClick={closeGiftModal}
                  className="rounded-full text-white"
                  style={{ background: primaryColor }}
                >
                  Entendi
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </>
  );
}



